import React, { useState, useEffect, useRef } from 'react';
import {
  User, Mail, Key, LogOut, Edit3, Trash2, Save, X, Camera, 
  AlertTriangle, CheckCircle, Activity, Lock, Eye, EyeOff, 
  Settings, Moon, Sun, Shield, ChevronDown, Bell, Home, 
  Calendar, Menu, Check, Info, Loader, ArrowRight, ArrowLeft, FileText,
  UserCheck, HelpCircle, Users, MessageCircle, 
  MessageSquare, 
  ListChecks, 
  BarChart2, 
  Link as LinkIcon, 
  Copy,
  XCircle,
  
} from 'lucide-react';

// API Configuration
const API_BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';

const UserManagementDashboard = ({ onClose, onLoginStatusChange, darkMode: propDarkMode, setDarkMode: setPropDarkMode }) => {
  // =========== STATE MANAGEMENT ===========
  // Authentication states
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [userData, setUserData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [activeCamp, setActiveCamp] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [activeAppointment, setActiveAppointment] = useState(null);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');
  const [activeTab, setActiveTab] = useState('questions');
const [questions, setQuestions] = useState([]);
const [questionStatistics, setQuestionStatistics] = useState([]);
const [newQuestion, setNewQuestion] = useState({
  questionText: '',
  isStarRating: false // Default to text response
});
const [feedbackLink, setFeedbackLink] = useState(null);

  // UI states
  const [activeView, setActiveView] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showSidebar, setShowSidebar] = useState(window.innerWidth > 768);
  const [inputFocus, setInputFocus] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [animateBackground, setAnimateBackground] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [profileImageError, setProfileImageError] = useState(false);
  const [darkMode, setDarkMode] = useState(propDarkMode !== undefined ? propDarkMode : false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Form data
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    username: '', 
    email: '', 
    password: '', 
    confirmPassword: '',
    tosAgreed: false 
  });
  const [editData, setEditData] = useState({
    username: '',
    email: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Refs
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  const sidebarRef = useRef(null);
  const backgroundRef = useRef(null);

  // =========== EFFECTS ===========
  // Window resize handler for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setShowSidebar(window.innerWidth > 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize state and check login status
  useEffect(() => {
    checkLoginStatus();
    
    // Add CSS animations and transitions to document
    addStylesToDocument();
    
    // Set up dark mode based on system preference or prop
    if (setPropDarkMode) {
      // If parent component controls dark mode
      setDarkMode(propDarkMode || false);
    } else {
      // Check for saved preference
      const savedDarkMode = localStorage.getItem('darkMode') === 'true';
      // Otherwise check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setDarkMode(savedDarkMode || prefersDark);
    }
    
    // Animate background after a slight delay
    setTimeout(() => setAnimateBackground(true), 500);
  }, [propDarkMode]);

  // Dark mode sync effect
  useEffect(() => {
    // Update parent if parent controls dark mode
    if (setPropDarkMode) {
      setPropDarkMode(darkMode);
    } else {
      // Otherwise save to localStorage
      localStorage.setItem('darkMode', darkMode.toString());
    }
    
    // Update background colors on body, html, and #root
    const backgroundColor = darkMode ? '#000000' : '#ffffff';
    document.body.style.backgroundColor = backgroundColor;
    document.documentElement.style.backgroundColor = backgroundColor;
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.backgroundColor = backgroundColor;
    }
    
    // Add or remove dark-mode class on document body
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode, setPropDarkMode]);

  // Fetch active camp when logged in
  useEffect(() => {
    if (isLoggedIn) {
      fetchActiveCamp();
      fetchNotifications();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    // Only set up the timer if the user is logged in and has a profile image path
    if (isLoggedIn && userData && userData.profileImagePath) {
      // Set up interval to reload the profile image every 30 seconds
      const profileImageRefreshInterval = setInterval(() => {
        loadProfileImage(userData.profileImagePath);
      }, 3000); // 30 seconds
      
      // Clean up the interval when component unmounts or when dependencies change
      return () => clearInterval(profileImageRefreshInterval);
    }
  }, [isLoggedIn, userData]);

  // Update active appointment tracking
  useEffect(() => {
    const updateActiveAppointment = () => {
      const now = new Date();
      if (!appointments || !appointments.length) return;
      
      // Check if the current camp phase is MAIN_CAMP
      const isMainCampPhase = activeCamp?.currentPhase?.phaseType === 'MAIN_CAMP';
      
      // Only track appointments if we're in the MAIN_CAMP phase
      if (isMainCampPhase) {
        const sortedAppointments = [...appointments].sort((a, b) => new Date(a.time) - new Date(b.time));
        
        const current = sortedAppointments.find(app => {
          const appTime = new Date(app.time);
          const endTime = new Date(appTime.getTime() + (app.duration || 60) * 60000);
          return appTime <= now && now <= endTime;
        });

        const next = sortedAppointments.find(app => {
          const appTime = new Date(app.time);
          return appTime > now;
        });

        setActiveAppointment(current);
        setNextAppointment(next);

        if (current) {
          const appTime = new Date(current.time);
          const endTime = new Date(appTime.getTime() + (current.duration || 60) * 60000);
          const remainingTime = endTime - now;
          const hours = Math.floor(remainingTime / 3600000);
          const minutes = Math.floor((remainingTime % 3600000) / 60000);
          setTimeLeft(hours > 0 
            ? `Noch ${hours}h ${minutes}min` 
            : `Noch ${minutes} Minuten`
          );
        } else if (next) {
          const appTime = new Date(next.time);
          const timeUntil = appTime - now;
          const hours = Math.floor(timeUntil / 3600000);
          const minutes = Math.floor((timeUntil % 3600000) / 60000);
          setTimeLeft(hours > 0 
            ? `In ${hours}h ${minutes}min` 
            : `In ${minutes} Minuten`
          );
        }
      } else {
        setActiveAppointment(null);
        setNextAppointment(null);
        setTimeLeft('');
      }
    };

    updateActiveAppointment();
    const timer = setInterval(updateActiveAppointment, 60000);
    return () => clearInterval(timer);
  }, [appointments, activeCamp]);
  const fetchQuestions = async () => {
    try {
      const response = await api('/api/feedback/questions');
      setQuestions(response);
    } catch (err) {
      showError('Failed to fetch questions');
    }
  };
  
  const handleCreateQuestion = async () => {
    try {
      if (!newQuestion.questionText) {
        showError('Question text cannot be empty');
        return;
      }
      
      setLoading(true);
      await api('/api/feedback/questions', 'POST', newQuestion);
      showSuccess('Question created successfully');
      setNewQuestion({
        questionText: '',
        isStarRating: true
      });
      await fetchQuestions();
      setLoading(false);
    } catch (err) {
      showError('Failed to create question');
      setLoading(false);
    }
  };
  
  const handleToggleQuestionStatus = async (questionId) => {
    try {
      setLoading(true);
      await api(`/api/feedback/questions/${questionId}/toggle`, 'PUT');
      showSuccess('Question status updated');
      await fetchQuestions();
      setLoading(false);
    } catch (err) {
      showError('Failed to update question status');
      setLoading(false);
    }
  };
  
  const handleDeleteQuestion = async (questionId) => {
    try {
      if (!confirm('Are you sure you want to delete this question? All responses will be deleted as well.')) {
        return;
      }
      
      setLoading(true);
      await api(`/api/feedback/questions/${questionId}`, 'DELETE');
      showSuccess('Question deleted');
      await fetchQuestions();
      await fetchQuestionStatistics();
      setLoading(false);
    } catch (err) {
      showError('Failed to delete question');
      setLoading(false);
    }
  };
  
  const fetchQuestionStatistics = async () => {
    try {
      setLoading(true);
      const response = await api('/api/feedback/statistics');
      setQuestionStatistics(response);
      setLoading(false);
    } catch (err) {
      showError('Failed to fetch statistics');
      setLoading(false);
    }
  };
  
  const handleGenerateLink = async () => {
    try {
      setLoading(true);
      const response = await api('/api/feedback/link');
      setFeedbackLink(response.link);
      showSuccess('Feedback link generated successfully');
      setLoading(false);
    } catch (err) {
      showError('Failed to generate link');
      setLoading(false);
    }
  };
  useEffect(() => {
    // existing code...
    
    // If viewing feedback management, fetch data
    if (isLoggedIn && activeView === 'feedback') {
      fetchQuestions();
      fetchQuestionStatistics();
    }
  }, [activeView]);
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => showSuccess('Link copied to clipboard'))
      .catch(() => showError('Failed to copy'));
  };

  const SamuraiRating = ({ value = 0, onChange, readOnly = false, size = 30 }) => {
    const [hoverValue, setHoverValue] = useState(0);
    
    const handleMouseEnter = (index) => {
      if (readOnly) return;
      setHoverValue(index);
    };
    
    const handleMouseLeave = () => {
      if (readOnly) return;
      setHoverValue(0);
    };
    
    const handleClick = (index) => {
      if (readOnly) return;
      onChange(index);
    };
  
    // Sunrise logo SVG from provided asset
    const samuraiIcon  = (filled, iconSize) => (
      <svg
        width={iconSize}
        height={(iconSize * 89) / 99}
        viewBox="0 0 99 89"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          transition: 'all 0.2s ease',
          filter: filled ? 'drop-shadow(0 0 2px rgba(218, 41, 28, 0.5))' : 'none',
        }}
      >
        <g clipPath="url(#clip0_915_12851)">
          <path
            d="M49.4149 0.247231C43.6992 0.246742 38.1118 1.94122 33.3592 5.11636C28.6066 8.2915 24.9023 12.8047 22.7148 18.0852C20.5273 23.3657 19.9548 29.1763 21.0697 34.7821C22.1847 40.388 24.937 45.5373 28.9786 49.5789C33.0201 53.6204 38.1695 56.3727 43.7753 57.4877C49.3812 58.6026 55.1918 58.0302 60.4722 55.8426C65.7527 53.6551 70.2659 49.9508 73.4411 45.1982C76.6162 40.4456 78.3107 34.8582 78.3102 29.1426C78.3023 21.4814 75.2555 14.1364 69.8383 8.71915C64.4211 3.30193 57.076 0.255084 49.4149 0.247231ZM49.4149 6.18056C53.2703 6.17855 57.0641 7.14788 60.446 8.99905C63.828 10.8502 66.689 13.5236 68.7651 16.7723C70.8411 20.021 72.0653 23.7404 72.3245 27.5871C72.5838 31.4338 71.8697 35.2839 70.2483 38.7818H28.579C26.9575 35.2837 26.2434 31.4334 26.5028 27.5865C26.7621 23.7396 27.9865 20.0201 30.0628 16.7713C32.1392 13.5225 35.0006 10.8492 38.3829 8.99816C41.7651 7.14716 45.5592 6.17814 49.4149 6.18056Z"
            fill={filled ? '#DA291C' : '#777777'}
          />
          <path
            d="M7.78997 88.5773C4.79117 88.5773 1.77258 87.4994 0 85.6403L3.026 82.1965C4.32876 83.4337 6.05274 84.13 7.8493 84.1446C9.55513 84.1446 10.6058 83.5018 10.6058 82.414C10.6058 81.3633 9.93833 80.9307 7.70097 80.152C4.17311 78.9307 1.02597 77.8503 1.02597 73.7563C1.02597 69.8923 4.0223 67.3755 8.84561 67.3755C11.2948 67.3473 13.6663 68.2342 15.4959 69.8626L12.5292 73.3064C11.5049 72.3486 10.1564 71.8134 8.75414 71.8082C7.15708 71.8082 6.21022 72.4362 6.21022 73.4572C6.21022 74.9999 8.78133 75.3212 11.3203 76.4387C14.1559 77.6748 15.884 79.1112 15.884 82.2336C15.8766 86.1891 12.9001 88.5773 7.78997 88.5773ZM22.3761 88.5773C18.915 88.5773 17.2833 86.2608 17.2833 82.1668V72.6414H22.3983V81.5092C22.3983 83.3057 23.0971 84.2048 24.4948 84.2064C25.805 84.2064 26.742 83.3461 26.742 81.2694V72.6414H31.8645V88.0977H26.8483L26.789 86.2114C26.0745 87.5513 24.6233 88.5773 22.3785 88.5773H22.3761ZM34.0276 72.6414H39.0314L39.0908 74.587C39.8497 73.21 41.3454 72.1617 43.4345 72.1617C46.8461 72.1617 48.6261 74.3867 48.6261 78.6909V88.0853H43.4963V79.2595C43.4963 77.4465 42.8073 76.5401 41.4295 76.5401C39.961 76.5401 39.1526 77.5463 39.1526 79.4746V88.1026H34.0301L34.0276 72.6414ZM50.6855 72.6414H55.6571L55.6868 75.4251C56.2554 73.7267 57.5162 72.3397 59.6102 72.3397C60.1378 72.3304 60.6638 72.4013 61.1702 72.5499V77.2224C60.5432 77.0589 59.8995 76.9685 59.2517 76.9529C56.7399 76.9529 55.8079 78.2929 55.8079 81.0271V88.0977H50.688L50.6855 72.6414ZM65.3952 71.3731C63.7067 71.3731 62.4903 70.137 62.4903 68.6537C62.4903 67.1703 63.6597 65.8971 65.3952 65.8971C67.0812 65.8971 68.2704 67.0838 68.2704 68.6537C68.2611 69.0213 68.1793 69.3835 68.0296 69.7194C67.8799 70.0553 67.6653 70.3583 67.3981 70.6111C67.1309 70.8638 66.8164 71.0612 66.4727 71.192C66.129 71.3227 65.7628 71.3843 65.3952 71.3731ZM62.8191 72.6315H67.9416V88.0878H62.8191V72.6315ZM76.001 88.5674C73.3558 88.5674 71.0739 87.8257 69.326 86.3202L71.8724 83.1755C72.9252 84.0229 74.2295 84.4968 75.5807 84.5229C76.7575 84.5229 77.3484 84.2138 77.3484 83.5933C77.3484 82.9728 76.4707 82.7157 75.0715 82.2756C73.3409 81.7342 72.0084 81.2521 71.0714 80.3151C70.2531 79.4968 69.8897 78.4807 69.8897 77.1235C69.8897 74.0876 72.2828 72.1519 76.2705 72.1519C78.4955 72.1519 80.5672 72.8391 82.0505 74.1568L79.5956 77.1532C78.891 76.5747 77.5635 76.1643 76.5696 76.1643C75.3335 76.1643 74.713 76.456 74.713 77.0345C74.713 77.613 75.4547 77.8998 77.2272 78.3819C80.2953 79.2125 82.2904 80.5797 82.2904 83.5735C82.2904 86.7627 79.7811 88.5773 76.001 88.5773V88.5674ZM91.8232 88.5674C85.7416 88.5674 83.3163 84.5006 83.3163 80.3596C83.3163 75.1976 86.906 72.1519 91.4054 72.1519C95.5242 72.1519 98.8221 74.6241 98.8221 79.88C98.8432 80.4521 98.8026 81.0249 98.701 81.5883H88.3671C88.6464 83.4697 89.979 84.555 92.1422 84.555C93.5387 84.5923 94.8978 84.1005 95.9469 83.178L98.3425 86.2336C96.5477 87.8801 94.5699 88.5773 91.8158 88.5773L91.8232 88.5674Z"
            fill={filled ? '#DA291C' : '#777777'}
          />
        </g>
        <defs>
          <clipPath id="clip0_915_12851">
            <rect width="98.8888" height="60" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  
    return (
      <div style={{
        display: 'flex',
        gap: '6px',
      }}>
        {[1, 2, 3, 4, 5,6,7,8,9,10].map(index => (
          <div
            key={index}
            style={{
              cursor: readOnly ? 'default' : 'pointer',
              transition: 'transform 0.2s ease',
              transform: (hoverValue >= index && !readOnly) ? 'scale(1.2)' : 'scale(1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleClick(index)}
          >
            {samuraiIcon((hoverValue || value) >= index, size)}
          </div>
        ))}
      </div>
    );
  };
  
  // =========== STYLE MANAGEMENT ===========
  // Add styles to document
  const addStylesToDocument = () => {
    if (!document.getElementById('user-management-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'user-management-styles';
      styleElement.innerHTML = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes slideDown {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .user-form-input:focus {
          outline: none;
          border-color: rgba(255, 59, 48, 0.5);
          box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.25);
        }
        
        .hover-scale {
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .hover-scale:hover {
          transform: scale(1.03);
        }
        
        @media (max-width: 768px) {
          .hidden-mobile {
            display: none !important;
          }
        }
        
        @media (min-width: 769px) {
          .hidden-desktop {
            display: none !important;
          }
        }
      `;
      document.head.appendChild(styleElement);
    }
  };

  // =========== API FUNCTIONS ===========
  // API helper function
  const api = async (endpoint, method = 'GET', data = null, headers = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
      ...(localStorage.getItem('auth_token') ? {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      } : {})
    };
    
    const requestOptions = {
      method,
      headers: { ...defaultHeaders, ...headers },
      ...(data && method !== 'GET' ? { body: JSON.stringify(data) } : {})
    };
    
    try {
      const response = await fetch(url, requestOptions);
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Something went wrong');
      }
      
      return result;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  };

  // Check if user is logged in
  const checkLoginStatus = async () => {
    try {
      setLoading(true);
      
      // Get token from localStorage
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setIsLoggedIn(false);
        setLoading(false);
        return;
      }
      
      // Verify token with backend
      try {
        const data = await api('/api/auth/get-profile');
        
        // Ensure that userData.id is available
        if (!data.user.id && data.user.email) {
          try {
            // Try to get user ID by email
            const userResponse = await fetch(`${API_BASE_URL}/api/users/byEmail/${data.user.email}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (userResponse.ok) {
              const userData = await userResponse.json();
              if (userData.id) {
                data.user.id = userData.id;
              }
            }
          } catch (idError) {
            console.error("Error fetching user ID:", idError);
          }
        }
        
        setUserData(data.user);
        setIsLoggedIn(true);
        setActiveView('dashboard');
        
        // Set profile image if available
        if (data.user.profileImagePath) {
          loadProfileImage(data.user.profileImagePath);
        } else {
          setImageLoading(false);
          setProfileImageError(true);
        }
        
        // Initialize edit form data
        setEditData({
          username: data.user.username || '',
          email: data.user.email || '',
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Notify parent component
        if (onLoginStatusChange) {
          onLoginStatusChange(true, data.user);
        }
      } catch (error) {
        // Invalid token
        localStorage.removeItem('auth_token');
        setIsLoggedIn(false);
        
        if (onLoginStatusChange) {
          onLoginStatusChange(false, null);
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error checking login status:', err);
      setIsLoggedIn(false);
      setLoading(false);
      
      if (onLoginStatusChange) {
        onLoginStatusChange(false, null);
      }
    }
  };
  const handleClose = () => {
    document.body.style.overflow = ''; // Restore scrolling
    if (onClose) onClose();
  };
  
  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      showError('Please enter email and password');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await api('/api/auth/login', 'POST', loginData);
      
      // Save token
      localStorage.setItem('auth_token', data.token);
      
      // Set user data
      setUserData(data.user);
      setIsLoggedIn(true);
      
      // Switch to dashboard view
      setActiveView('dashboard');
      
      // Set profile image if available
      if (data.user.profileImagePath) {
        loadProfileImage(data.user.profileImagePath);
      } else {
        setImageLoading(false);
        setProfileImageError(true);
      }
      
      // Initialize edit form data
      setEditData({
        username: data.user.username || '',
        email: data.user.email || '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      showSuccess('Login successful!');
      
      // Notify parent component
      if (onLoginStatusChange) {
        onLoginStatusChange(true, data.user);
      }
      
      setLoading(false);
    } catch (err) {
      showError(err.message || 'Login failed: Connection problem');
      setLoading(false);
    }
  };
  const renderFeedbackManagementView = () => (
    <div style={styles.dashboardContainer}>
      <div style={{
        ...styles.card,
        padding: windowWidth < 576 ? '20px' : '30px',
      }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: 800,
          marginBottom: '30px',
          color: theme.colors.primary,
          background: theme.gradients.primary,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>Feedback Management</h2>
        
        <div style={{
          display: 'flex',
          marginBottom: '20px',
          borderBottom: `1px solid ${theme.colors.divider}`,
          overflowX: 'auto',
          paddingBottom: '10px',
        }}>
          <button 
            style={{
              padding: '10px 20px',
              background: activeTab === 'questions' ? theme.gradients.primary : 'transparent',
              color: activeTab === 'questions' ? '#fff' : theme.colors.textPrimary,
              border: 'none',
              borderRadius: theme.borderRadius.medium,
              marginRight: '10px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            onClick={() => setActiveTab('questions')}
          >
            Questions
          </button>
          <button 
            style={{
              padding: '10px 20px',
              background: activeTab === 'statistics' ? theme.gradients.primary : 'transparent',
              color: activeTab === 'statistics' ? '#fff' : theme.colors.textPrimary,
              border: 'none',
              borderRadius: theme.borderRadius.medium,
              marginRight: '10px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            onClick={() => setActiveTab('statistics')}
          >
            Statistics
          </button>
          <button 
            style={{
              padding: '10px 20px',
              background: activeTab === 'links' ? theme.gradients.primary : 'transparent',
              color: activeTab === 'links' ? '#fff' : theme.colors.textPrimary,
              border: 'none',
              borderRadius: theme.borderRadius.medium,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            onClick={() => setActiveTab('links')}
          >
            Shareable Links
          </button>
        </div>
        
        {activeTab === 'questions' && renderQuestionsTab()}
        {activeTab === 'statistics' && renderStatisticsTab()}
        {activeTab === 'links' && renderLinksTab()}
      </div>
    </div>
  );
  
  // Questions Tab
  const renderQuestionsTab = () => (
    <div>
      <div style={{
        ...styles.card,
        marginBottom: '20px',
        boxShadow: 'none',
        background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: 600,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: theme.colors.textPrimary,
        }}>
          <MessageCircle size={20} style={{ color: theme.colors.primary }} />
          Create New Question
        </h3>
        
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>
            Question Text
          </label>
          <input
            type="text"
            style={{
              ...styles.formInput,
              ...(inputFocus === 'question-text' && styles.formInputFocused)
            }}
            placeholder="Enter your question here"
            value={newQuestion.questionText}
            onChange={(e) => setNewQuestion({...newQuestion, questionText: e.target.value})}
            onFocus={() => setInputFocus('question-text')}
            onBlur={() => setInputFocus(null)}
          />
        </div>
        
        <div style={{
          marginBottom: '20px',
        }}>
          <label style={{
            ...styles.formLabel,
            marginBottom: '12px',
          }}>
            Response Type
          </label>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '15px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '10px 15px',
              borderRadius: theme.borderRadius.medium,
              background: newQuestion.isStarRating ? 
                (darkMode ? 'rgba(255, 59, 48, 0.15)' : 'rgba(255, 59, 48, 0.1)') : 
                (darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'),
              border: `1px solid ${newQuestion.isStarRating ? 
                'rgba(255, 59, 48, 0.3)' : theme.colors.divider}`,
              transition: theme.transitions.medium,
            }}
            onClick={() => setNewQuestion({...newQuestion, isStarRating: true})}
            >
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: `2px solid ${newQuestion.isStarRating ? 
                  theme.colors.primary : theme.colors.dividerStrong}`,
                marginRight: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {newQuestion.isStarRating && <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: theme.colors.primary,
                }}></div>}
              </div>
              <div>
                <div style={{
                  fontWeight: 500,
                  marginBottom: '2px',
                  color: theme.colors.textPrimary,
                }}>Sunrise Rating (1-5)</div>
                <div style={{
                  fontSize: '12px',
                  color: theme.colors.textSecondary,
                }}>User selects 1-5 Sunrise logos</div>
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '10px 15px',
              borderRadius: theme.borderRadius.medium,
              background: !newQuestion.isStarRating ? 
                (darkMode ? 'rgba(255, 59, 48, 0.15)' : 'rgba(255, 59, 48, 0.1)') : 
                (darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'),
              border: `1px solid ${!newQuestion.isStarRating ? 
                'rgba(255, 59, 48, 0.3)' : theme.colors.divider}`,
              transition: theme.transitions.medium,
            }}
            onClick={() => setNewQuestion({...newQuestion, isStarRating: false})}
            >
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: `2px solid ${!newQuestion.isStarRating ? 
                  theme.colors.primary : theme.colors.dividerStrong}`,
                marginRight: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {!newQuestion.isStarRating && <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: theme.colors.primary,
                }}></div>}
              </div>
              <div>
                <div style={{
                  fontWeight: 500,
                  marginBottom: '2px',
                  color: theme.colors.textPrimary,
                }}>Text Response</div>
                <div style={{
                  fontSize: '12px',
                  color: theme.colors.textSecondary,
                }}>User writes free-form text</div>
              </div>
            </div>
          </div>
        </div>
        
        <button 
          style={{
            ...styles.button,
            ...styles.primaryButton,
            ...(loading && styles.buttonDisabled)
          }}
          onClick={handleCreateQuestion}
          disabled={loading || !newQuestion.questionText}
        >
          {loading ? (
            <>
              <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
              Creating...
            </>
          ) : (
            <>Create Question</>
          )}
        </button>
      </div>
      
      <div style={{
        ...styles.card,
        boxShadow: 'none',
        background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: 600,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: theme.colors.textPrimary,
        }}>
          <ListChecks size={20} style={{ color: theme.colors.primary }} />
          Existing Questions
        </h3>
        
        {questions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '30px 20px',
            color: theme.colors.textSecondary,
          }}>
            <MessageCircle size={40} style={{ marginBottom: '10px', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>No questions yet. Create one above!</p>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}>
            {questions.map(question => (
              <div key={question.id} style={{
                padding: '15px',
                background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.5)',
                borderRadius: theme.borderRadius.medium,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px',
                border: `1px solid ${theme.colors.divider}`,
                transition: theme.transitions.medium,
              }}>
                <div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 500,
                    marginBottom: '5px',
                    color: theme.colors.textPrimary,
                  }}>{question.questionText}</div>
                  <div style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                    }}>
                      {question.isStarRating ? (
                        <>
                          <SamuraiRating value={3} readOnly size={14} />
                          <span>Rating</span>
                        </>
                      ) : (
                        <>
                          <MessageSquare size={12} />
                          <span>Text</span>
                        </>
                      )}
                    </span>
                    
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      background: question.isActive ? 
                        (darkMode ? 'rgba(52, 199, 89, 0.15)' : 'rgba(52, 199, 89, 0.1)') :
                        (darkMode ? 'rgba(255, 69, 58, 0.15)' : 'rgba(255, 69, 58, 0.1)'),
                      color: question.isActive ? '#34C759' : '#FF453A',
                    }}>
                      {question.isActive ? (
                        <>
                          <CheckCircle size={12} />
                          <span>Active</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={12} />
                          <span>Inactive</span>
                        </>
                      )}
                    </span>
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '8px',
                }}>
                  <button 
                    style={{
                      padding: '8px 12px',
                      background: question.isActive ? 
                        (darkMode ? 'rgba(52, 199, 89, 0.15)' : 'rgba(52, 199, 89, 0.1)') :
                        (darkMode ? 'rgba(255, 69, 58, 0.15)' : 'rgba(255, 69, 58, 0.1)'),
                      color: question.isActive ? '#34C759' : '#FF453A',
                      border: `1px solid ${question.isActive ? 
                        'rgba(52, 199, 89, 0.3)' : 'rgba(255, 69, 58, 0.3)'}`,
                      borderRadius: theme.borderRadius.medium,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      transition: theme.transitions.medium,
                    }}
                    onClick={() => handleToggleQuestionStatus(question.id)}
                    disabled={loading}
                  >
                    {question.isActive ? (
                      <>
                        <CheckCircle size={14} /> Active
                      </>
                    ) : (
                      <>
                        <XCircle size={14} /> Inactive
                      </>
                    )}
                  </button>
                  
                  <button 
                    style={{
                      padding: '8px 12px',
                      background: darkMode ? 'rgba(255, 69, 58, 0.15)' : 'rgba(255, 69, 58, 0.1)',
                      color: '#FF453A',
                      border: '1px solid rgba(255, 69, 58, 0.3)',
                      borderRadius: theme.borderRadius.medium,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      transition: theme.transitions.medium,
                    }}
                    onClick={() => handleDeleteQuestion(question.id)}
                    disabled={loading}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
  
  // Statistics Tab
  const renderStatisticsTab = () => (
    <div>
      {questionStatistics.length === 0 ? (
        <div style={{
          ...styles.card,
          textAlign: 'center',
          padding: '40px 20px',
          boxShadow: 'none',
          background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
        }}>
          <BarChart2 size={40} style={{ marginBottom: '10px', opacity: 0.5 }} />
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '10px',
            color: theme.colors.textPrimary,
          }}>No Feedback Data Yet</h3>
          <p style={{
            color: theme.colors.textSecondary,
            maxWidth: '500px',
            margin: '0 auto',
          }}>
            Once users submit feedback, you'll see statistics here. 
            Share the feedback link to start collecting responses.
          </p>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}>
          {questionStatistics.map(stats => (
            <div key={stats.questionId} style={{
              ...styles.card,
              boxShadow: 'none',
              background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: '15px',
                color: theme.colors.textPrimary,
              }}>{stats.questionText}</h3>
              
              <div style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <Users size={16} />
                {stats.responseCount} {stats.responseCount === 1 ? 'response' : 'responses'}
              </div>
              
              {stats.isStarRating ? (
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '20px',
                    padding: '15px',
                    borderRadius: theme.borderRadius.medium,
                    background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.5)',
                    border: `1px solid ${theme.colors.divider}`,
                  }}>
                    <div style={{
                      fontSize: '36px',
                      fontWeight: 700,
                      marginRight: '15px',
                      color: theme.colors.primary,
                      width: '80px',
                      textAlign: 'center',
                    }}>
                      {stats.averageRating ? stats.averageRating.toFixed(1) : '0.0'}
                    </div>
                    <div>
                      <SamuraiRating value={Math.round(stats.averageRating || 0)} readOnly />
                      <div style={{
                        fontSize: '14px',
                        color: theme.colors.textSecondary,
                        marginTop: '5px',
                      }}>
                        Average rating
                      </div>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    padding: '15px',
                    borderRadius: theme.borderRadius.medium,
                    background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.5)',
                    border: `1px solid ${theme.colors.divider}`,
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      marginBottom: '5px',
                      color: theme.colors.textPrimary,
                    }}>
                      Rating Distribution
                    </div>
                    
                    {[10,9,8,7,6,5, 4, 3, 2, 1].map(rating => (
                      <div key={rating} style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                        <div style={{
                          width: '30px',
                          marginRight: '10px',
                          textAlign: 'center',
                          color: theme.colors.textSecondary,
                          fontSize: '14px',
                        }}>{rating}</div>
                        <div style={{
                          flex: 1,
                          height: '12px',
                          background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          margin: '0 10px',
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${((stats.ratingDistribution && stats.ratingDistribution[rating]) ? 
                              stats.ratingDistribution[rating] / stats.responseCount : 0) * 100}%`,
                            background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
                            borderRadius: '6px',
                            minWidth: '4px',
                          }}></div>
                        </div>
                        <div style={{
                          width: '40px',
                          textAlign: 'center',
                          fontWeight: '500',
                          color: theme.colors.textPrimary,
                        }}>
                          {(stats.ratingDistribution && stats.ratingDistribution[rating]) || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}>
                  {stats.textResponses && stats.textResponses.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: theme.colors.textSecondary,
                      background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.5)',
                      borderRadius: theme.borderRadius.medium,
                      border: `1px solid ${theme.colors.divider}`,
                    }}>
                      No text responses yet.
                    </div>
                  ) : (
                    stats.textResponses && stats.textResponses.map((response, index) => (
                      <div key={index} style={{
                        padding: '15px',
                        background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.5)',
                        borderRadius: theme.borderRadius.medium,
                        border: `1px solid ${theme.colors.divider}`,
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                        }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #FF3B30, #FF2D55)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '600',
                            flexShrink: 0,
                          }}>
                            {index + 1}
                          </div>
                          <div style={{
                            color: theme.colors.textPrimary,
                            lineHeight: 1.5,
                          }}>
                            {response}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
  
  // Links Tab
  const renderLinksTab = () => (
    <div style={{
      ...styles.card,
      boxShadow: 'none',
      background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
    }}>
      <h3 style={{
        fontSize: '20px',
        fontWeight: 600,
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: theme.colors.textPrimary,
      }}>
        <LinkIcon size={20} style={{ color: theme.colors.primary }} />
        Feedback Links
      </h3>
      
      <p style={{
        marginBottom: '20px',
        color: theme.colors.textSecondary,
      }}>
        Generate and share links for users to submit feedback anonymously.
      </p>
      
      <button 
        style={{
          ...styles.button,
          ...styles.primaryButton,
          marginBottom: '20px',
          ...(loading && styles.buttonDisabled)
        }}
        onClick={handleGenerateLink}
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Generating...
          </>
        ) : (
          <>
            <LinkIcon size={18} />
            Generate Shareable Link
          </>
        )}
      </button>
      
      {feedbackLink && (
        <div style={{
          padding: '15px',
          background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.5)',
          borderRadius: theme.borderRadius.medium,
          border: `1px solid ${theme.colors.divider}`,
          marginBottom: '20px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '10px',
          }}>
            <div style={{
              wordBreak: 'break-all',
              flex: 1,
              minWidth: '200px',
              padding: '8px 12px',
              background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              borderRadius: theme.borderRadius.medium,
              fontSize: '14px',
              color: theme.colors.textPrimary,
            }}>
              {feedbackLink}
            </div>
            <button 
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(to right, #007AFF, #5AC8FA)',
                color: '#fff',
                border: 'none',
                borderRadius: theme.borderRadius.medium,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 500,
                transition: theme.transitions.medium,
                boxShadow: '0 2px 8px rgba(0, 122, 255, 0.25)',
              }}
              onClick={() => copyToClipboard(feedbackLink)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 122, 255, 0.25)';
              }}
            >
              <Copy size={16} />
              Copy Link
            </button>
          </div>
        </div>
      )}
      
      <div style={{
        padding: '20px',
        background: 'rgba(52, 199, 89, 0.1)',
        borderRadius: theme.borderRadius.medium,
        border: '1px solid rgba(52, 199, 89, 0.3)',
        marginBottom: '20px',
      }}>
        <div style={{
          fontSize: '16px',
          fontWeight: 500,
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          color: '#34C759',
        }}>
          <div style={{
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: 'rgba(52, 199, 89, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <CheckCircle size={18} color="#34C759" />
          </div>
          Feedback is completely anonymous
        </div>
        <p style={{
          margin: 0,
          color: darkMode ? 'rgba(220, 250, 235, 0.8)' : 'rgba(30, 90, 50, 0.8)',
          lineHeight: 1.5,
        }}>
          Users who use this link will be able to submit feedback without revealing their identity.
          We don't track or store any personally identifiable information with the responses.
        </p>
      </div>
      
      <div style={{
        padding: '20px',
        background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.5)',
        borderRadius: theme.borderRadius.medium,
        border: `1px solid ${theme.colors.divider}`,
      }}>
        <h4 style={{
          fontSize: '16px',
          fontWeight: 600,
          marginBottom: '10px',
          color: theme.colors.textPrimary,
        }}>How to use feedback links:</h4>
        
        <ol style={{
          color: theme.colors.textSecondary,
          paddingLeft: '20px',
          margin: 0,
        }}>
          <li style={{ marginBottom: '8px' }}>
            Generate a link using the button above
          </li>
          <li style={{ marginBottom: '8px' }}>
            Copy and share it with users via email, chat, or other communication channels
          </li>
          <li style={{ marginBottom: '8px' }}>
            Users will access the feedback form where they can provide anonymous feedback
          </li>
          <li>
            Check the Statistics tab to view the results
          </li>
        </ol>
      </div>
    </div>
  );
  // Logout handler
  const handleLogout = async () => {
    try {
      setLoading(true);
      
      // Remove token
      localStorage.removeItem('auth_token');
      
      // Reset state
      setIsLoggedIn(false);
      setUserData(null);
      setProfileImage(null);
      setActiveView('login');
      setActiveCamp(null);
      setNotifications([]);
      setAppointments([]);
      
      // Reset login data
      setLoginData({ email: '', password: '' });
      
      showSuccess('Logout successful');
      
      // Notify parent component
      if (onLoginStatusChange) {
        onLoginStatusChange(false, null);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error during logout:', err);
      
      // Logout anyway in case of error
      localStorage.removeItem('auth_token');
      setIsLoggedIn(false);
      setUserData(null);
      setActiveView('login');
      
      if (onLoginStatusChange) {
        onLoginStatusChange(false, null);
      }
      
      setLoading(false);
    }
  };
  
  // Registration handler
  // Registration handler
const handleRegister = async (e) => {
  e.preventDefault();
  
  // Validation
  if (!registerData.username || !registerData.email || !registerData.password || !registerData.confirmPassword) {
    showError('Please fill in all fields');
    return;
  }
  
  if (registerData.password !== registerData.confirmPassword) {
    showError('Passwords do not match');
    return;
  }
  
  if (registerData.password.length < 6) {
    showError('Password must be at least 6 characters long');
    return;
  }
  
  if (!registerData.tosAgreed) {
    showError('Please agree to the Terms of Service');
    return;
  }
  
  // Check if email domain is allowed
  const emailDomain = registerData.email.split('@')[1];
  if (emailDomain !== 'bbcag.ch' && emailDomain !== 'sunrise.net') {
    showError('Not a valid employ email');
    return;
  }
  
  try {
    setLoading(true);
    setError(null);
    
    // Create a FormData object for multipart/form-data
    const formData = new FormData();
    
    // Convert user data to JSON string
    const userJson = JSON.stringify({
      username: registerData.username,
      email: registerData.email,
      password: registerData.password
    });
    
    // Append user data
    formData.append('user', userJson);
    
    // Create a blank image as placeholder
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    
    // Convert canvas to blob and append to formData
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    formData.append('profileImage', blob, 'profile.jpg');
    
    // Send registration request
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      body: formData
    });
    
    const data = await response.json();
    
    if (response.ok && data.token) {
      showSuccess('Registration successful! You can now log in.');
      
      // Switch to login view
      setActiveView('login');
      
      // Pre-fill login data
      setLoginData({
        email: registerData.email,
        password: registerData.password
      });
      
      // Reset registration data
      setRegisterData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        tosAgreed: false
      });
    } else {
      showError(data.message || 'Registration failed');
    }
    
    setLoading(false);
  } catch (err) {
    showError('Registration failed: ' + (err.message || 'Connection problem'));
    setLoading(false);
  }
};
  // Profile update handler
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      // Validation
      if (!editData.username || !editData.email) {
        throw new Error('Username and email are required');
      }
      
      // Create FormData for multipart request
      const formData = new FormData();
      formData.append('username', editData.username);
      formData.append('email', editData.email);
      
      // Send request to update profile
      const response = await fetch(`${API_BASE_URL}/api/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });
      
      const profileData = await response.json();
      
      // If setting a new password
      if (editData.newPassword) {
        if (!editData.oldPassword) {
          throw new Error('Please enter your current password');
        }
        
        if (editData.newPassword !== editData.confirmPassword) {
          throw new Error('New passwords do not match');
        }
        
        if (editData.newPassword.length < 6) {
          throw new Error('New password must be at least 6 characters long');
        }
        
        // Change password
        await api('/api/auth/change-password', 'PUT', {
          oldPassword: editData.oldPassword,
          newPassword: editData.newPassword
        });
      }
      
      if (profileData.user) {
        // Update user data
        setUserData(profileData.user);
        
        // Exit edit mode
        setActiveView('profile');
        
        showSuccess('Profile successfully updated');
        
        // Notify parent component
        if (onLoginStatusChange) {
          onLoginStatusChange(true, profileData.user);
        }
      } else {
        throw new Error('Update failed');
      }
      
      setLoading(false);
    } catch (err) {
      showError(err.message || 'Error updating profile');
      setLoading(false);
    }
  };
  const FloatingCloseButton = ({ onClick }) => (
    <div 
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 2500,
        background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
        borderRadius: '50%',
        width: '60px',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 5px 20px rgba(255, 59, 48, 0.5)',
        cursor: 'pointer',
        animation: 'pulse 2s infinite ease-in-out',
        border: '2px solid rgba(255, 255, 255, 0.7)',
        transform: 'scale(1)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.1)';
        e.currentTarget.style.boxShadow = '0 8px 30px rgba(255, 59, 48, 0.7)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 5px 20px rgba(255, 59, 48, 0.5)';
      }}
    >
      <X size={30} color="#ffffff" />
    </div>
  );
  
  // 2. Create a top header back button
  const BackButton = ({ onClick }) => (
    <button
      style={{
        position: 'fixed',
        top: '20px',
        left: '150px',
        zIndex: 2500,
        background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
        borderRadius: '12px',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        boxShadow: '0 4px 15px rgba(255, 59, 48, 0.4)',
        cursor: 'pointer',
        border: 'none',
        color: '#ffffff',
        fontWeight: 'bold',
        fontSize: '16px',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 59, 48, 0.6)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 59, 48, 0.4)';
      }}
    >
      <ArrowLeft size={18} />
      Zurück
    </button>
  );
  
  // Account deletion handler
  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      setDeleteError('');
      
      if (!deletePassword) {
        setDeleteError('Please enter your password');
        setLoading(false);
        return;
      }
      
      // Get token from localStorage
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Direct call to delete-account API
      const response = await fetch(`${API_BASE_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: deletePassword
        })
      });
      
      if (response.ok) {
        // Account deleted successfully
        localStorage.removeItem('auth_token');
        
        // Reset state
        setIsLoggedIn(false);
        setUserData(null);
        setProfileImage(null);
        setActiveView('login');
        setShowDeleteConfirm(false);
        
        showSuccess('Your account has been successfully deleted');
        
        // Notify parent component
        if (onLoginStatusChange) {
          onLoginStatusChange(false, null);
        }
      } else {
        // Get error message from server
        const errorData = await response.json();
        throw new Error(errorData.message || 'Incorrect password or error deleting account');
      }
    } catch (err) {
      setDeleteError(err.message || 'Error deleting account');
    } finally {
      setLoading(false);
    }
  };

  // Load profile image with error handling and cache busting
  const loadProfileImage = (imagePath) => {
    setImageLoading(true);
    setProfileImageError(false);
    
    // Add timestamp for cache busting
    const timestamp = new Date().getTime();
    const imageUrl = `${API_BASE_URL}${imagePath}?t=${timestamp}`;
    
    const img = new Image();
    
    img.onload = () => {
      setProfileImage(imageUrl);
      setImageLoading(false);
    };
    
    img.onerror = () => {
      setProfileImageError(true);
      setImageLoading(false);
    };
    
    img.src = imageUrl;
  };
  
  // Profile image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.match('image.*')) {
      showError('Please select an image (JPEG, PNG)');
      return;
    }
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('Image too large (max 5MB)');
      return;
    }
    
    try {
      setLoading(true);
      setImageLoading(true);
      
      const formData = new FormData();
      formData.append('profileImage', file);
      
      const response = await fetch(`${API_BASE_URL}/api/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.user && data.user.profileImagePath) {
        // Load the profile image
        loadProfileImage(data.user.profileImagePath);
        
        // Update user data
        setUserData(data.user);
        
        showSuccess('Profile image successfully uploaded');
        
        // Notify parent component
        if (onLoginStatusChange) {
          onLoginStatusChange(true, data.user);
        }
      } else {
        showError(data.message || 'Upload failed');
        setImageLoading(false);
      }
      
      setLoading(false);
    } catch (err) {
      showError('Error uploading: ' + (err.message || 'Connection problem'));
      setLoading(false);
      setImageLoading(false);
    }
  };
  
  // Fetch active camp
  const fetchActiveCamp = async () => {
    try {
      // First try to get current camp
      try {
        const data = await api('/api/camps/current');
        
        // Check camp phase
        const now = new Date();
        let currentPhase = null;
        
        if (data.phases) {
          currentPhase = data.phases.find(phase => {
            const phaseStart = new Date(phase.startDate);
            const phaseEnd = new Date(phase.endDate);
            return now >= phaseStart && now <= phaseEnd && phase.enabled;
          });
        }
        
        setActiveCamp({
          ...data,
          currentPhase: currentPhase
        });
        
        // If camp has schedule, format it for our use - but only if we're in MAIN_CAMP phase
        if (data && data.schedule && Array.isArray(data.schedule) && 
            currentPhase && currentPhase.phaseType === 'MAIN_CAMP') {
          const formattedAppointments = data.schedule
            .filter(item => item.date && item.title)
            .map(item => ({
              id: item.id || Math.random().toString(36).substring(2, 11),
              label: item.title,
              time: new Date(`${item.date}T${item.startTime || '00:00'}`),
              duration: item.endTime ? 
                (new Date(`${item.date}T${item.endTime}`).getTime() - 
                 new Date(`${item.date}T${item.startTime}`).getTime()) / (1000 * 60) : 60,
              location: item.location || '',
              description: item.description || ''
            }))
            .sort((a, b) => new Date(a.time) - new Date(b.time));
            
          setAppointments(formattedAppointments);
        } else {
          setAppointments([]);
        }
        return;
      } catch (error) {
        console.warn('No current camp found, checking all camps...');
      }
      
      // If no current camp or error, try to get all camps
      const allCampsResponse = await api('/api/camps/all');
      const allCamps = allCampsResponse;
      
      const now = new Date();
      
      // Find the camp that is currently active
      const activeCamp = allCamps.find(camp => {
        const startDate = new Date(camp.startDate);
        const endDate = new Date(camp.endDate);
        return now >= startDate && now <= endDate;
      });
      
      if (activeCamp) {
        // Check camp phase
        let currentPhase = null;
        
        if (activeCamp.phases) {
          currentPhase = activeCamp.phases.find(phase => {
            const phaseStart = new Date(phase.startDate);
            const phaseEnd = new Date(phase.endDate);
            return now >= phaseStart && now <= phaseEnd && phase.enabled;
          });
        }
        
        setActiveCamp({
          ...activeCamp,
          currentPhase: currentPhase
        });
        
        // If camp has schedule, format it for our use - but only if we're in MAIN_CAMP phase
        if (activeCamp.schedule && Array.isArray(activeCamp.schedule) && 
            currentPhase && currentPhase.phaseType === 'MAIN_CAMP') {
          const formattedAppointments = activeCamp.schedule
            .filter(item => item.date && item.title)
            .map(item => ({
              id: item.id || Math.random().toString(36).substring(2, 11),
              label: item.title,
              time: new Date(`${item.date}T${item.startTime || '00:00'}`),
              duration: item.endTime ? 
                (new Date(`${item.date}T${item.endTime}`).getTime() - 
                 new Date(`${item.date}T${item.startTime}`).getTime()) / (1000 * 60) : 60,
              location: item.location || '',
              description: item.description || ''
            }))
            .sort((a, b) => new Date(a.time) - new Date(b.time));
            
          setAppointments(formattedAppointments);
        } else {
          setAppointments([]);
        }
      } else {
        setActiveCamp(null);
      }
    } catch (err) {
      console.error('Error in fetchActiveCamp:', err);
      setActiveCamp(null);
    }
  };
  
  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const data = await api('/api/auth/notifications');
      
      setNotifications(data.notifications || []);
      
      // Count unread notifications
      const unreadCount = (data.notifications || []).filter(n => !n.isRead).length;
      setUnreadNotificationCount(unreadCount);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };
  
  // Fetch unread notification count
  const fetchUnreadNotificationCount = async () => {
    try {
      if (!userData?.id) return;
      
      const data = await api(`/api/users/${userData.id}/notifications/unread-count`);
      
      setUnreadNotificationCount(data.count || 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };
  
  // Mark notification as read
  const markNotificationAsRead = async (notificationId) => {
    try {
      await api(`/api/auth/notifications/${notificationId}/read`, 'POST');
      
      // Refresh notifications
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  // =========== HELPER FUNCTIONS ===========
  // Show error message
  const showError = (message, duration = 5000) => {
    setError(message);
    setTimeout(() => setError(null), duration);
  };
  
  // Show success message
  const showSuccess = (message, duration = 5000) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), duration);
  };
  
  // Change view
  const changeView = (view) => {
    setActiveView(view);
    
    // On mobile, close sidebar when changing views
    if (window.innerWidth < 768) {
      setShowSidebar(false);
    }
    
    // If switching to dashboard, fetch fresh data
    if (view === 'dashboard' && isLoggedIn) {
      fetchNotifications();
      fetchActiveCamp();
    }
  };
  
  // Toggle file input for profile image
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  
  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Format time
  const formatTime = (time) => {
    const date = new Date(time);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Format time span (start and end time)
  const formatTimeSpan = (startTime, duration) => {
    const start = new Date(startTime);
    const end = new Date(start.getTime() + (duration || 60) * 60000);
    return `${formatTime(start)} - ${formatTime(end)}`;
  };
  
  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };
  
  // Generate profile color based on username
  const generateProfileColor = (username) => {
    if (!username) return '#FF3B30';
    
    const colors = [
      '#FF3B30', '#FF9500', '#FFCC00', '#4CD964',
      '#5AC8FA', '#007AFF', '#5856D6', '#FF2D55'
    ];
    
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
      hash = username.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };
  
  // Determine if text should be light or dark based on background
  const useLightText = (hexColor) => {
    if (!hexColor) return true;
    
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    // Calculate brightness (standard formula)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
  };

  // Get activity icon based on appointment label
  const getActivityIcon = (label) => {
    if (!label) return <Activity size={24} />;
    
    const lowerLabel = label.toLowerCase();
    
    if (lowerLabel.includes('essen') || lowerLabel.includes('mittag') || 
        lowerLabel.includes('abend') || lowerLabel.includes('frühstück')) {
      return <Activity size={24} />;
    } else if (lowerLabel.includes('treffen') || lowerLabel.includes('meeting') || 
               lowerLabel.includes('besprechung')) {
      return <Activity size={24} />;
    } else if (lowerLabel.includes('pause') || lowerLabel.includes('break') || 
               lowerLabel.includes('freizeit')) {
      return <Activity size={24} />;
    } else if (lowerLabel.includes('workshop') || lowerLabel.includes('kurs') || 
               lowerLabel.includes('seminar')) {
      return <Activity size={24} />;
    } else if (lowerLabel.includes('sport') || lowerLabel.includes('aktivität') || 
               lowerLabel.includes('spiel')) {
      return <Activity size={24} />;
    } else if (lowerLabel.includes('check') || lowerLabel.includes('prüfung') || 
               lowerLabel.includes('kontrolle')) {
      return <Check size={24} />;
    } else {
      return <Activity size={24} />;
    }
  };

  // Get appropriate grid based on screen size
  const getResponsiveGrid = (columns) => {
    if (windowWidth < 576) {
      return {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '16px',
      };
    } else if (windowWidth < 768) {
      return {
        display: 'grid',
        gridTemplateColumns: columns > 2 ? 'repeat(2, 1fr)' : `repeat(${columns}, 1fr)`,
        gap: '16px',
      };
    } else if (windowWidth < 992) {
      return {
        display: 'grid',
        gridTemplateColumns: columns > 3 ? 'repeat(3, 1fr)' : `repeat(${columns}, 1fr)`,
        gap: '16px',
      };
    } else {
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(columns, 4)}, 1fr)`,
        gap: '16px',
      };
    }
  };

  // Create a handler for pre-camp view
  const handlePreCampView = () => {
    if (!activeCamp || !activeCamp.currentPhase) {
      return (
        <div style={{ ...styles.card, ...(darkMode && styles.cardDark) }}>
          <h2 style={{ ...styles.sectionTitle, ...(darkMode && styles.sectionTitleDark) }}>
            <Info size={20} />
            No Active Camp
          </h2>
          <p style={{ color: darkMode ? '#d1d5db' : '#4b5563', textAlign: 'center', padding: '40px 0' }}>
            There is currently no active camp available.
          </p>
        </div>
      );
    }

    const phaseType = activeCamp.currentPhase.phaseType;
    if (phaseType !== 'PRE_CAMP') {
      return (
        <div style={{ ...styles.card, ...(darkMode && styles.cardDark) }}>
          <h2 style={{ ...styles.sectionTitle, ...(darkMode && styles.sectionTitleDark) }}>
            <Info size={20} />
            Phase Information
          </h2>
          <p style={{ color: darkMode ? '#d1d5db' : '#4b5563', textAlign: 'center', padding: '20px 0' }}>
            This camp is not in the preparation phase.
          </p>
        </div>
      );
    }

    return (
      <div style={{ ...styles.card, ...(darkMode && styles.cardDark) }}>
        <h2 style={{ ...styles.sectionTitle, ...(darkMode && styles.sectionTitleDark) }}>
          <Calendar size={20} />
          Pre-Camp Phase: {activeCamp.title}
        </h2>
        <div style={{ padding: '20px', background: darkMode ? '#374151' : '#f9fafb', borderRadius: '12px' }}>
          <p style={{ color: darkMode ? '#d1d5db' : '#4b5563', marginBottom: '20px', lineHeight: 1.6 }}>
            {activeCamp.currentPhase.additionalInfo || 
             'Welcome to the preparation phase! Here you\'ll find important information to prepare for the main camp.'}
          </p>
          
          {activeCamp.currentPhase.packingItems && activeCamp.currentPhase.packingItems.length > 0 && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: darkMode ? '#f9fafb' : '#1f2937' }}>
                Packing List
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {activeCamp.currentPhase.packingItems.map((item, index) => (
                  <div key={index} style={{ 
                    padding: '12px', 
                    background: darkMode ? '#1f2937' : '#ffffff', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' 
                  }}>
                    • {item.item}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Create a handler for post-camp view
  const handlePostCampView = () => {
    if (!activeCamp || !activeCamp.currentPhase) {
      return (
        <div style={{ ...styles.card, ...(darkMode && styles.cardDark) }}>
          <h2 style={{ ...styles.sectionTitle, ...(darkMode && styles.sectionTitleDark) }}>
            <Info size={20} />
            No Active Camp
          </h2>
          <p style={{ color: darkMode ? '#d1d5db' : '#4b5563', textAlign: 'center', padding: '40px 0' }}>
            There is currently no active camp available.
          </p>
        </div>
      );
    }

    const phaseType = activeCamp.currentPhase.phaseType;
    if (phaseType !== 'POST_CAMP') {
      return (
        <div style={{ ...styles.card, ...(darkMode && styles.cardDark) }}>
          <h2 style={{ ...styles.sectionTitle, ...(darkMode && styles.sectionTitleDark) }}>
            <Info size={20} />
            Phase Information
          </h2>
          <p style={{ color: darkMode ? '#d1d5db' : '#4b5563', textAlign: 'center', padding: '20px 0' }}>
            This camp is not in the post-camp phase.
          </p>
        </div>
      );
    }

    return (
      <div style={{ ...styles.card, ...(darkMode && styles.cardDark) }}>
        <h2 style={{ ...styles.sectionTitle, ...(darkMode && styles.sectionTitleDark) }}>
          <Calendar size={20} />
          Post-Camp Phase: {activeCamp.title}
        </h2>
        <div style={{ 
          padding: '20px', 
          background: 'linear-gradient(135deg, #D42F2F, #E67E22)', 
          borderRadius: '12px', 
          color: '#ffffff', 
          marginBottom: '20px' 
        }}>
          <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '10px' }}>
            {activeCamp.title} - Post-Camp
          </h3>
          <p style={{ lineHeight: 1.6 }}>
            {activeCamp.currentPhase.additionalInfo || 
             'Thank you for your participation! The post-camp phase helps us evaluate the camp and improve future camps.'}
          </p>
        </div>
        
        {activeCamp.currentPhase.activityItems && activeCamp.currentPhase.activityItems.length > 0 && (
          <div>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              marginBottom: '15px', 
              color: darkMode ? '#f9fafb' : '#1f2937' 
            }}>
              Post-Camp Activities
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {activeCamp.currentPhase.activityItems.map((item, index) => (
                <div key={index} style={{ 
                  padding: '15px', 
                  background: darkMode ? '#374151' : '#f9fafb', 
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #E67E22, #D42F2F)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff'
                  }}>
                    <Activity size={20} />
                  </div>
                  <div>
                    <p style={{ color: darkMode ? '#f9fafb' : '#1f2937', fontWeight: '500' }}>
                      {item.activity}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // =========== STYLING ===========
  // Define the theme
  const theme = {
    colors: {
      primary: '#FF3B30', 
      primaryDark: '#E62E26',
      primaryLight: '#FF6058',
      secondary: '#FF2D55',
      secondaryDark: '#E6294D',
      secondaryLight: '#FF577D',
      
      background: darkMode ? '#121212' : '#F8F9FA',
      surfaceLight: darkMode ? '#1E1E1E' : '#FFFFFF',
      surfaceMedium: darkMode ? '#252525' : '#F2F2F2',
      surfaceDark: darkMode ? '#2D2D2D' : '#E8E8E8',
      
      textPrimary: darkMode ? '#FFFFFF' : '#1A1A1A',
      textSecondary: darkMode ? '#AAAAAA' : '#6F6F6F',
      textTertiary: darkMode ? '#777777' : '#A0A0A0',
      
      divider: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      dividerStrong: darkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
      
      error: '#FF453A',
      success: '#34C759',
      warning: '#FFC10F',
      info: '#007AFF',
      
      overlay: darkMode ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.5)'
    },
    
    shadows: {
      small: darkMode ? 
        '0 2px 5px rgba(0, 0, 0, 0.3)' : 
        '0 2px 5px rgba(0, 0, 0, 0.1)',
      medium: darkMode ? 
        '0 4px 15px rgba(0, 0, 0, 0.4)' : 
        '0 4px 15px rgba(0, 0, 0, 0.15)',
      large: darkMode ? 
        '0 10px 30px rgba(0, 0, 0, 0.5)' : 
        '0 10px 30px rgba(0, 0, 0, 0.2)',
      button: darkMode ?
        '0 4px 12px rgba(255, 59, 48, 0.35)' :
        '0 4px 12px rgba(255, 59, 48, 0.25)',
      buttonHover: darkMode ?
        '0 6px 20px rgba(255, 59, 48, 0.45)' :
        '0 6px 20px rgba(255, 59, 48, 0.35)',
    },
    
    transitions: {
      fast: 'all 0.2s ease',
      medium: 'all 0.3s ease',
      slow: 'all 0.5s ease',
      bounce: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    
    gradients: {
      primary: 'linear-gradient(to right, #FF3B30, #FF2D55)',
      subtle: darkMode ?
        'linear-gradient(to right, rgba(255, 59, 48, 0.15), rgba(255, 45, 85, 0.15))' :
        'linear-gradient(to right, rgba(255, 59, 48, 0.1), rgba(255, 45, 85, 0.1))',
      surface: darkMode ?
        'linear-gradient(135deg, rgba(30, 30, 30, 0.95), rgba(25, 25, 25, 0.95))' :
        'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 248, 248, 0.95))',
      glass: darkMode ?
        'linear-gradient(135deg, rgba(40, 40, 40, 0.8), rgba(30, 30, 30, 0.8))' :
        'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(245, 245, 245, 0.8))',
    },
    
    borderRadius: {
      small: '8px',
      medium: '12px',
      large: '16px',
      extraLarge: '24px',
      circle: '50%',
    },
    
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px',
      xxl: '48px',
    },
    
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      
      h1: {
        fontSize: '32px',
        fontWeight: '700',
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '28px',
        fontWeight: '700',
        lineHeight: 1.25,
      },
      h3: {
        fontSize: '24px',
        fontWeight: '600',
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '20px',
        fontWeight: '600',
        lineHeight: 1.35,
      },
      body: {
        fontSize: '16px',
        fontWeight: '400',
        lineHeight: 1.5,
      },
      caption: {
        fontSize: '14px',
        fontWeight: '400',
        lineHeight: 1.4,
      },
      small: {
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: 1.4,
      },
      button: {
        fontSize: '16px',
        fontWeight: '600',
        lineHeight: 1.4,
      },
    },
    
    breakpoints: {
      sm: '576px',
      md: '768px',
      lg: '992px',
      xl: '1200px',
    },
  };

  // Centralized styles
  const styles = {
    // Core styles
    container: {
      display: 'flex',
      flexDirection: windowWidth < 768 ? 'column' : 'row',
      minHeight: '100vh',
      width: '100%',
      backgroundColor: theme.colors.background,
      color: theme.colors.textPrimary,
      fontFamily: theme.typography.fontFamily,
      overflow: 'auto', // Changed from 'hidden' to 'auto'
      position: 'fixed', // Changed from 'absolute' to 'fixed'
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 999, // Changed from "999px" to 999
    },
    mainContent: {
      flex: 1,
      padding: windowWidth < 576 ? 
        `${theme.spacing.md} ${theme.spacing.sm}` : 
        `${theme.spacing.lg} ${theme.spacing.md}`,
      overflowY: 'auto',
      maxWidth: '100%',
      transition: theme.transitions.medium,
      marginLeft: showSidebar && windowWidth > 768 ? '280px' : '0',
      backgroundColor: theme.colors.background,
      position: 'relative',
      animation: 'fadeIn 0.5s ease',
      // marginTop: "0px", // Changed from "100px" to "0px"
      height: '100%', // Added height
      marginTop: "100px",

    },
  
  
    
    // Background & decorative elements
    backgroundGradient: {
      position: 'fixed', // Changed to fixed to stay in place
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: darkMode ?
        'radial-gradient(circle at 100% 0%, rgba(255, 59, 48, 0.15) 0%, transparent 50%), radial-gradient(circle at 0% 100%, rgba(255, 45, 85, 0.15) 0%, transparent 50%)' :
        'radial-gradient(circle at 100% 0%, rgba(255, 59, 48, 0.1) 0%, transparent 50%), radial-gradient(circle at 0% 100%, rgba(255, 45, 85, 0.1) 0%, transparent 50%)',
      opacity: animateBackground ? 0.8 : 0,
      zIndex: 0,
      filter: 'blur(80px)',
      transition: 'opacity 1.5s ease-in-out',
      pointerEvents: 'none',
    },
    backgroundPattern: {
      position: 'fixed', // Changed to fixed to stay in place
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: darkMode 
        ? 'radial-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px)'
        : 'radial-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px)',
      backgroundSize: '20px 20px',
      zIndex: 0,
      opacity: animateBackground ? 0.4 : 0,
      transition: 'opacity 1s ease-in-out',
      pointerEvents: 'none',
    },
    
    // Sidebar
    sidebar: {
      position: 'fixed',
      top: windowWidth <= 768 ? '60px' : '0',
      left: '0',
      width: '280px',
      height: windowWidth <= 768 ? 'calc(100vh - 60px)' : '100vh',
      backgroundColor: darkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRight: `1px solid ${theme.colors.divider}`,
      boxShadow: darkMode ? '5px 0 15px rgba(0, 0, 0, 0.15)' : '5px 0 15px rgba(0, 0, 0, 0.05)',
      zIndex: 100,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      overflowX: 'hidden',
      transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      transform: showSidebar ? 'translateX(0)' : 'translateX(-100%)',
      padding: 0,
    },
    sidebarHeader: {
      padding: `${theme.spacing.lg} ${theme.spacing.md}`,
      borderBottom: `1px solid ${theme.colors.divider}`,
      background: theme.gradients.glass,
    },
    logoText: {
      fontSize: '28px',
      fontWeight: 700,
      background: theme.gradients.primary,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: theme.spacing.md,
      display: 'inline-block',
      textShadow: darkMode ? '0 2px 10px rgba(255, 59, 48, 0.3)' : 'none',
    },
    sidebarProfile: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.md,
      padding: theme.spacing.md,
      borderRadius: theme.borderRadius.medium,
      background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      border: `1px solid ${theme.colors.divider}`,
      transition: theme.transitions.medium,
    },
    profileAvatar: {
      position: 'relative',
      width: '48px',
      height: '48px',
      borderRadius: theme.borderRadius.circle,
      overflow: 'hidden',
    },
    profileImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: theme.borderRadius.circle,
    },
    profileInitials: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '600',
      fontSize: '18px',
      borderRadius: theme.borderRadius.circle,
    },
    profileStatus: {
      position: 'absolute',
      bottom: '2px',
      right: '2px',
      width: '10px',
      height: '10px',
      borderRadius: theme.borderRadius.circle,
      background: theme.colors.success,
      border: darkMode ? '2px solid rgba(30, 30, 30, 0.95)' : '2px solid rgba(255, 255, 255, 0.95)',
    },
    profileInfo: {
      flex: 1,
      overflow: 'hidden',
    },
    profileName: {
      fontWeight: '600',
      fontSize: '15px',
      color: theme.colors.textPrimary,
      margin: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    profileEmail: {
      fontSize: '13px',
      color: theme.colors.textSecondary,
      margin: '4px 0 0 0',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    
    // Menu
    menuSection: {
      padding: `${theme.spacing.md} ${theme.spacing.sm}`,
      flex: 1, // Take up available space
    },
    menuCategory: {
      fontSize: '11px',
      fontWeight: '600',
      color: theme.colors.textTertiary,
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      margin: `${theme.spacing.md} 0 ${theme.spacing.xs}`,
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.md,
      padding: `${theme.spacing.md} ${theme.spacing.md}`,
      borderRadius: theme.borderRadius.medium,
      color: theme.colors.textSecondary,
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: theme.transitions.medium,
      marginBottom: theme.spacing.xs,
      width: '100%',
      textAlign: 'left',
      background: 'transparent',
      border: 'none',
      position: 'relative',
      overflow: 'hidden',
    },
    menuItemActive: {
      background: darkMode ? 'rgba(255, 59, 48, 0.1)' : 'rgba(255, 59, 48, 0.05)',
      color: theme.colors.primary,
      fontWeight: '600',
    },
    
    // Menu item hover states added as JavaScript handlers for better interactivity
    
    menuIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      color: 'currentColor',
      opacity: 0.9,
      transition: theme.transitions.medium,
    },
    
    menuIndicator: {
      position: 'absolute',
      left: '0',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '3px',
      height: '20px',
      background: theme.colors.primary,
      borderRadius: '0 3px 3px 0',
    },
    
    // Footer elements
    sidebarFooter: {
      padding: theme.spacing.md,
      borderTop: `1px solid ${theme.colors.divider}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    darkModeToggle: {
      width: '40px',
      height: '40px',
      borderRadius: theme.borderRadius.medium,
      background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
      border: `1px solid ${theme.colors.divider}`,
      color: theme.colors.textSecondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: theme.transitions.medium,
    },
    footerText: {
      fontSize: '12px',
      color: theme.colors.textTertiary,
    },
    
    // Mobile header
    mobileHeader: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: '60px',
      background: darkMode ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderBottom: `1px solid ${theme.colors.divider}`,
      zIndex: 101,
      padding: `0 ${theme.spacing.md}`,
      display: windowWidth <= 768 ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: theme.shadows.small,
    },
    mobileHeaderTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: theme.colors.textPrimary,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    mobileMenuButton: {
      width: '40px',
      height: '40px',
      borderRadius: theme.borderRadius.medium,
      background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
      border: `1px solid ${theme.colors.divider}`,
      color: theme.colors.textSecondary,
      // display: 'flex',
      paddingLeft:8,
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: theme.transitions.medium,
    },
    
    // Form styles
    formContainer: {
      maxWidth: '500px',
      width: '100%',
      margin: `${theme.spacing.xl} auto`,
      padding: windowWidth < 576 ? theme.spacing.lg : theme.spacing.xl,
      background: theme.gradients.glass,
      borderRadius: theme.borderRadius.extraLarge,
      backdropFilter: 'blur(10px)',
      border: `1px solid ${theme.colors.divider}`,
      boxShadow: theme.shadows.large,
      animation: 'scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
      position: 'relative',
      overflow: 'hidden',
    },
    formHeader: {
      textAlign: 'center',
      marginBottom: theme.spacing.xl,
    },
    formTitle: {
      fontSize: theme.typography.h2.fontSize,
      fontWeight: theme.typography.h2.fontWeight,
      color: theme.colors.textPrimary,
      margin: `0 0 ${theme.spacing.xs} 0`,
    },
    formSubtitle: {
      fontSize: theme.typography.body.fontSize,
      color: theme.colors.textSecondary,
      margin: 0,
    },
    formGroup: {
      marginBottom: theme.spacing.lg,
    },
    formLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
      marginBottom: theme.spacing.sm,
      fontSize: '14px',
      fontWeight: '500',
      color: theme.colors.textSecondary,
    },
    formInput: {
      width: '100%',
      padding: `${theme.spacing.md} ${theme.spacing.md}`,
      background: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.02)',
      border: `1px solid ${theme.colors.divider}`,
      borderRadius: theme.borderRadius.medium,
      color: theme.colors.textPrimary,
      fontSize: '16px',
      transition: theme.transitions.medium,
      boxShadow: 'none',
    },
    formInputFocused: {
      borderColor: theme.colors.primary,
      boxShadow: darkMode ? 
        '0 0 0 3px rgba(255, 59, 48, 0.2)' : 
        '0 0 0 3px rgba(255, 59, 48, 0.15)',
      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.03)',
    },
    passwordInput: {
      position: 'relative',
    },
    passwordToggle: {
      position: 'absolute',
      right: theme.spacing.md,
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'transparent',
      border: 'none',
      color: theme.colors.textTertiary,
      cursor: 'pointer',
      display: 'flex',
      padding: theme.spacing.xs,
    },
    formHint: {
      fontSize: '12px',
      color: theme.colors.textTertiary,
      margin: `${theme.spacing.xs} 0 0 0`,
    },
    formFooter: {
      marginTop: theme.spacing.xl,
      textAlign: 'center',
    },
    formDivider: {
      display: 'flex',
      alignItems: 'center',
      margin: `${theme.spacing.lg} 0`,
      color: theme.colors.textTertiary,
      fontSize: '14px',
    },
    dividerLine: {
      flex: 1,
      height: '1px',
      background: theme.colors.divider,
    },
    dividerText: {
      padding: `0 ${theme.spacing.md}`,
    },
    formLink: {
      color: theme.colors.primary,
      fontWeight: '500',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      transition: theme.transitions.fast,
      padding: 0,
      fontSize: 'inherit',
      textDecoration: 'none',
    },
    
    // Button styles
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
      padding: `${theme.spacing.md} ${theme.spacing.md}`,
      borderRadius: theme.borderRadius.medium,
      fontSize: theme.typography.button.fontSize,
      fontWeight: theme.typography.button.fontWeight,
      cursor: 'pointer',
      transition: theme.transitions.medium,
      width: '100%',
      border: 'none',
    },
    primaryButton: {
      background: theme.gradients.primary,
      color: '#FFFFFF',
      boxShadow: theme.shadows.button,
    },
    secondaryButton: {
      background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
      color: theme.colors.textPrimary,
      border: `1px solid ${theme.colors.divider}`,
    },
    dangerButton: {
      background: 'rgba(255, 59, 48, 0.1)',
      color: theme.colors.error,
      border: `1px solid ${darkMode ? 'rgba(255, 59, 48, 0.3)' : 'rgba(255, 59, 48, 0.2)'}`,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    
    // Card styles
    card: {
      background: theme.gradients.glass,
      borderRadius: theme.borderRadius.large,
      boxShadow: theme.shadows.medium,
      border: `1px solid ${theme.colors.divider}`,
      backdropFilter: 'blur(10px)',
      transition: theme.transitions.medium,
      padding: theme.spacing.lg,
      marginBottom: theme.spacing.lg,
      overflow: 'hidden',
      position: 'relative',
    },
    cardDark: {
      background: darkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    },
    cardHeader: {
      marginBottom: theme.spacing.md,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.md,
    },
    cardTitle: {
      fontSize: theme.typography.h4.fontSize,
      fontWeight: theme.typography.h4.fontWeight,
      color: theme.colors.textPrimary,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    
    // Notification styles
    notification: {
      position: 'fixed',
      bottom: theme.spacing.xl,
      left: '50%',
      transform: 'translateX(-50%)',
      maxWidth: '400px',
      width: 'calc(100% - 32px)',
      padding: theme.spacing.md,
      borderRadius: theme.borderRadius.medium,
      boxShadow: theme.shadows.large,
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing.md,
      zIndex: 1000,
      animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    },
    notificationError: {
      background: darkMode ? 'rgba(255, 69, 58, 0.15)' : 'rgba(255, 69, 58, 0.1)',
      border: `1px solid ${darkMode ? 'rgba(255, 69, 58, 0.3)' : 'rgba(255, 69, 58, 0.2)'}`,
    },
    notificationSuccess: {
      background: darkMode ? 'rgba(52, 199, 89, 0.15)' : 'rgba(52, 199, 89, 0.1)',
      border: `1px solid ${darkMode ? 'rgba(52, 199, 89, 0.3)' : 'rgba(52, 199, 89, 0.2)'}`,
    },
    notificationIcon: {
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    notificationContent: {
      flex: 1,
    },
    notificationMessage: {
      margin: 0,
      fontSize: '14px',
      lineHeight: 1.5,
      color: theme.colors.textPrimary,
    },
    
    // Modal styles
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.colors.overlay,
      backdropFilter: 'blur(5px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing.lg,
      animation: 'fadeIn 0.3s ease',
    },
    modalContent: {
      background: theme.gradients.glass,
      borderRadius: theme.borderRadius.extraLarge,
      maxWidth: '500px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: theme.shadows.large,
      border: `1px solid ${theme.colors.divider}`,
      animation: 'scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    modalHeader: {
      padding: theme.spacing.lg,
      borderBottom: `1px solid ${theme.colors.divider}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    modalTitle: {
      margin: 0,
      fontSize: theme.typography.h3.fontSize,
      fontWeight: theme.typography.h3.fontWeight,
      color: theme.colors.textPrimary,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    modalBody: {
      padding: theme.spacing.lg,
    },
    modalFooter: {
      padding: theme.spacing.lg,
      borderTop: `1px solid ${theme.colors.divider}`,
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing.md,
    },
    
    // Dashboard specific styles
    dashboardContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: `0 ${theme.spacing.md}`,
    },
    welcomeBanner: {
      background: `linear-gradient(135deg, rgba(255, 59, 48, 0.1), rgba(255, 45, 85, 0.1))`,
      borderRadius: theme.borderRadius.large,
      padding: theme.spacing.xl,
      marginBottom: theme.spacing.xl,
      border: `1px solid ${darkMode ? 'rgba(255, 59, 48, 0.2)' : 'rgba(255, 59, 48, 0.1)'}`,
      boxShadow: darkMode ? 
        '0 10px 30px rgba(255, 59, 48, 0.1)' : 
        '0 10px 30px rgba(255, 59, 48, 0.05)',
      position: 'relative',
      overflow: 'hidden',
      animation: 'fadeIn 0.8s ease',
    },
    welcomeTitle: {
      fontSize: '32px',
      fontWeight: 800,
      color: theme.colors.textPrimary,
      marginBottom: theme.spacing.sm,
      animation: 'slideDown 0.5s ease 0.3s both',
    },
    welcomeText: {
      fontSize: '18px',
      color: theme.colors.textSecondary,
      maxWidth: '700px',
      lineHeight: 1.6,
      animation: 'slideDown 0.5s ease 0.5s both',
    },
    
    // Feature cards
    featureGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: theme.spacing.lg,
      marginBottom: theme.spacing.xl,
    },
    featureCard: {
      cursor: 'pointer',
      padding: theme.spacing.xl,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing.md,
      textAlign: 'center',
      borderRadius: theme.borderRadius.large,
      background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
      border: `1px solid ${theme.colors.divider}`,
      transition: theme.transitions.bounce,
      position: 'relative',
      overflow: 'hidden',
    },
    featureIcon: {
      width: '60px',
      height: '60px',
      borderRadius: theme.borderRadius.large,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.secondary}20)`,
      color: theme.colors.primary,
      transition: theme.transitions.bounce,
    },
    featureTitle: {
      fontSize: '20px',
      fontWeight: '600',
      margin: `${theme.spacing.sm} 0 0`,
      color: theme.colors.textPrimary,
    },
    featureDescription: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
      margin: 0,
    },
    
    // Profile styles
    profileHeader: {
      textAlign: 'center',
      marginBottom: theme.spacing.xl,
    },
    profileImageContainer: {
      position: 'relative',
      width: '150px',
      height: '150px',
      margin: `0 auto ${theme.spacing.md}`,
      animation: 'scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
    },
    profileLargeImage: {
      width: '100%',
      height: '100%',
      borderRadius: theme.borderRadius.circle,
      objectFit: 'cover',
      border: `3px solid ${darkMode ? theme.colors.surfaceMedium : '#fff'}`,
      boxShadow: theme.shadows.medium,
    },
    profileLargeInitials: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: theme.borderRadius.circle,
      fontSize: '48px',
      fontWeight: '700',
      border: `3px solid ${darkMode ? theme.colors.surfaceMedium : '#fff'}`,
      boxShadow: theme.shadows.medium,
    },
    profileImageButton: {
      position: 'absolute',
      bottom: '5px',
      right: '5px',
      width: '40px',
      height: '40px',
      borderRadius: theme.borderRadius.circle,
      background: theme.gradients.primary,
      border: `3px solid ${darkMode ? theme.colors.surfaceMedium : '#fff'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      cursor: 'pointer',
      transition: theme.transitions.medium,
      boxShadow: theme.shadows.small,
      zIndex: 1,
    },
    profileHeaderName: {
      fontSize: '28px',
      fontWeight: '700',
      margin: `0 0 ${theme.spacing.xs}`,
      color: theme.colors.textPrimary,
    },
    profileHeaderEmail: {
      fontSize: '16px',
      margin: 0,
      color: theme.colors.textSecondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
    },
    profileInfoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: theme.spacing.md,
      marginBottom: theme.spacing.xl,
    },
    profileInfoCard: {
      padding: theme.spacing.md,
      background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
      borderRadius: theme.borderRadius.medium,
      border: `1px solid ${theme.colors.divider}`,
    },
    profileInfoLabel: {
      fontSize: '12px',
      color: theme.colors.textTertiary,
      marginBottom: '4px',
    },
    profileInfoValue: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme.colors.textPrimary,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    profileActions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: theme.spacing.md,
      marginTop: theme.spacing.xl,
    },
    
    // Section specific styles
    sectionTitle: {
      fontSize: '20px', 
      fontWeight: 700, 
      marginBottom: '20px', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px', 
      color: darkMode ? '#f9fafb' : '#1f2937'
    },
    sectionTitleDark: {
      color: darkMode ? '#f9fafb' : '#1f2937'
    },
    
    // Calendar styles
    campHeader: {
      background: darkMode ? 'rgba(55, 65, 81, 0.7)' : 'rgba(249, 250, 251, 0.7)',
      borderRadius: '20px',
      padding: '24px',
      marginBottom: '28px',
      border: darkMode ? '1px solid #4b5563' : '1px solid #e5e7eb',
      boxShadow: darkMode ? '0 8px 30px rgba(0, 0, 0, 0.2)' : '0 8px 30px rgba(0, 0, 0, 0.05)',
      backdropFilter: 'blur(10px)',
    },
    appointmentCard: {
      backgroundColor: darkMode ? 'rgba(55, 65, 81, 0.7)' : 'rgba(249, 250, 251, 0.7)',
      borderRadius: '16px',
      marginBottom: '16px',
      padding: windowWidth < 576 ? '16px' : '20px',
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: darkMode ? '0 4px 15px rgba(0, 0, 0, 0.2)' : '0 4px 15px rgba(0, 0, 0, 0.08)',
      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease',
      flexWrap: 'wrap',
      backdropFilter: 'blur(8px)',
      border: '1px solid',
      borderColor: darkMode ? 'rgba(75, 85, 99, 0.5)' : 'rgba(229, 231, 235, 0.8)',
    },
    appointmentCardActive: {
      borderLeft: '4px solid #FF3B30',
      backgroundColor: darkMode ? 'rgba(255, 59, 48, 0.1)' : 'rgba(255, 59, 48, 0.05)',
    },
    appointmentCardNext: {
      borderLeft: '4px solid #3b82f6',
      backgroundColor: darkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
    },
    appointmentDetails: {
      flex: 1, 
      paddingRight: '12px',
      minWidth: '200px',
    },
    appointmentTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: darkMode ? '#f9fafb' : '#1f2937',
      marginBottom: '6px',
    },
  };

  // Delete confirmation modal
  const DeleteConfirmationModal = () => {
    // Use useRef to maintain focus on the input field
    const passwordInputRef = useRef(null);
    
    // Focus the input field when the modal opens
    useEffect(() => {
      if (passwordInputRef.current) {
        passwordInputRef.current.focus();
      }
    }, []);
  
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(5px)',
          zIndex: 2000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          // animation: 'fadeIn 0.3s ease',
        }}
      >
        <div style={{
          background: darkMode ? 'rgba(28, 28, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
          }}>
            <h3 style={{
              margin: 0,
              color: darkMode ? '#fff' : '#000',
              fontSize: '20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={24} color="#ef4444" />
              Delete Account
            </h3>
            <button 
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                setShowDeleteConfirm(false);
              }}
            >
              <X size={20} />
            </button>
          </div>
  
          <div style={{
            padding: '20px 24px',
          }}>
            <div style={{
              padding: '20px',
              borderRadius: '10px',
              background: darkMode ? 'rgba(255, 69, 58, 0.1)' : 'rgba(255, 69, 58, 0.05)',
              marginBottom: '20px',
            }}>
              <p style={{
                margin: 0,
                color: darkMode ? '#f3f4f6' : '#1f2937',
                lineHeight: 1.6,
                fontSize: '15px',
              }}>
                <strong>Warning:</strong> This action cannot be undone. All your data will be permanently deleted.
              </p>
            </div>
  
            <div style={{
              marginBottom: '20px',
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: darkMode ? '#d1d5db' : '#4b5563',
              }}>
                <Lock size={16} />
                Enter your password to confirm
              </label>
              <div style={{
                position: 'relative',
              }}>
                <input 
                  type={showPassword ? "text" : "password"}
                  ref={passwordInputRef}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    paddingRight: '40px', // Space for the eye icon
                    background: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.02)',
                    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    borderRadius: '10px',
                    color: darkMode ? '#f3f4f6' : '#1f2937',
                    fontSize: '16px',
                    outline: 'none', // Remove default focus outline
                  }}
                  value={deletePassword}
                  onChange={(e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    setDeletePassword(e.target.value);
                  }}
                  placeholder="Enter your password"
                />
                <button 
                  type="button" 
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    color: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                    cursor: 'pointer',
                    display: 'flex',
                    padding: '4px',
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    setShowPassword(!showPassword);
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
  
            {deleteError && (
              <div style={{
                padding: '10px 15px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                marginBottom: '20px',
                borderLeft: '3px solid #ef4444',
                color: '#ef4444'
              }}>
                {deleteError}
              </div>
            )}
          </div>
  
          <div style={{
            padding: '16px 24px',
            borderTop: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}>
            <button 
              style={{
                padding: '10px 20px',
                background: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                border: 'none',
                borderRadius: '10px',
                color: darkMode ? '#f3f4f6' : '#1f2937',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                setShowDeleteConfirm(false);
              }}
            >
              Cancel
            </button>
  
            <button 
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
                border: 'none',
                borderRadius: '10px',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                opacity: loading || !deletePassword ? 0.7 : 1,
                pointerEvents: loading || !deletePassword ? 'none' : 'auto',
              }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                handleDeleteAccount();
              }}
              disabled={loading || !deletePassword}
            >
              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Loader size={16} style={{animation: 'spin 1s linear infinite'}} />
                  Deleting...
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Trash2 size={16} />
                  Delete Account
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };
  const TermsOfService = ({ showTerms, setShowTerms }) => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(5px)',
      zIndex: 2000,
      display: showTerms ? 'flex' : 'none',
      justifyContent: 'center',
      alignItems: 'center',
      // animation: 'fadeIn 0.3s ease',
      overflow: 'auto'
    }}>
      <div style={{
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        background: 'rgba(28, 28, 30, 0.95)',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        {/* Modal Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <h3 style={{
            margin: 0,
            color: '#fff',
            fontSize: '20px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FileText size={24} style={{ color: '#FF3B30' }} />
            Terms of Service
          </h3>
          <button 
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onClick={() => setShowTerms(false)}
          >
            <X size={20} />
          </button>
        </div>
  
        {/* Modal Content - Scrollable */}
        <div style={{
          padding: '20px',
          maxHeight: 'calc(90vh - 150px)',
          overflowY: 'auto',
        }}>
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#fff',
            marginTop: 0,
            marginBottom: '16px',
          }}>1. Acceptance of Terms</h4>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            marginBottom: '24px',
          }}>
            By using the cAmPP ("App"), you (the "User") accept the following terms of service. If you do not agree to these terms, you may not use the App.
          </p>
          
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '16px',
          }}>2. Registration and Account</h4>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            marginBottom: '24px',
          }}>
            To use the App, you must create an account and register. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account.
          </p>
          
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '16px',
          }}>3. Privacy</h4>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            marginBottom: '24px',
          }}>
            The collection and use of your personal data is subject to our Privacy Policy, which can be viewed in the App.
          </p>
          
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '16px',
          }}>4. Code of Conduct</h4>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            marginBottom: '12px',
          }}>
            When using the App, you agree to:
          </p>
          <ul style={{
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            marginBottom: '24px',
            paddingLeft: '24px',
          }}>
            <li style={{ marginBottom: '8px' }}>Not conduct illegal or unethical activities</li>
            <li style={{ marginBottom: '8px' }}>Respect the rights of other users</li>
            <li style={{ marginBottom: '8px' }}>Not share content that is offensive, defamatory, or inappropriate</li>
            <li style={{ marginBottom: '8px' }}>Not misuse or disrupt the App</li>
          </ul>
          
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '16px',
          }}>5. Limitation of Liability</h4>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            marginBottom: '24px',
          }}>
            The App is provided "as is" without any warranties. We are not liable for damages arising from the use or inability to use the App.
          </p>
          
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '16px',
          }}>6. Changes to Terms</h4>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            marginBottom: '24px',
          }}>
            We reserve the right to change these terms of service at any time. The updated terms will be published in the App.
          </p>
          
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '16px',
          }}>7. Termination</h4>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            marginBottom: '16px',
          }}>
            We reserve the right to terminate or suspend a user's access to the App at any time and for any reason.
          </p>
  
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            marginBottom: '16px',
          }}>
            Further legal information can be found at:  
            <a 
              href="https://www.sunrise.ch/en/general/legal-matters/data-protection" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: '#FF3B30',
                textDecoration: 'none',
                marginLeft: '6px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#FF5E54';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#FF3B30';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Sunrise Terms of Service
            </a>
          </p>
        </div>
  
        {/* Modal Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button 
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
              border: 'none',
              borderRadius: '12px',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onClick={() => setShowTerms(false)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 59, 48, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
  

  const PrivacyPolicy = ({ showPrivacyPolicy, setShowPrivacyPolicy }) => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(5px)',
      zIndex: 2000,
      display: showPrivacyPolicy ? 'flex' : 'none',
      justifyContent: 'center',
      alignItems: 'center',
      // animation: 'fadeIn 0.3s ease',
      overflow: 'auto'
    }}>
      <div style={{
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        background: 'rgba(28, 28, 30, 0.95)',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        {/* Modal Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <h3 style={{
            margin: 0,
            color: '#fff',
            fontSize: '20px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Shield size={24} style={{ color: '#FF3B30' }} />
            Privacy Notice
          </h3>
          <button 
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onClick={() => setShowPrivacyPolicy(false)}
          >
            <X size={20} />
          </button>
        </div>
  
        {/* Modal Content - Scrollable */}
        <div style={{
          padding: '20px',
          maxHeight: 'calc(90vh - 150px)',
          overflowY: 'auto',
          color: 'rgba(255, 255, 255, 0.7)',
          lineHeight: 1.6,
          fontSize: '14px',
        }}>
          <p>
            With the following Privacy Notice, Sunrise LLC (hereinafter "Sunrise" "us" or "we") informs you about which personal data (hereinafter "Data") is collected and processed when using our website, obtaining services as a customer (concluded service contract) or receiving promotions (e.g. newsletter, giveaways). This Privacy Notice also applies to the services offered under the brand names yallo, Lebara, MySports, swype and UPC.
          </p>
          <p>
            Please note that this Privacy Notice may be updated from time to time. We therefore recommend that you check our website regularly for any updates.
          </p>
  
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#fff',
            marginTop: '24px',
            marginBottom: '16px',
          }}>1. Processing Purposes</h4>
          <p>
            The available Data will primarily be used by us to fulfill our contractual obligations, to acquire new customers, to ensure the smoothest possible operation of our services, to contribute to the improvement of our services, to enable marketing and analyses, and to comply with our legal and regulatory obligations, this includes in particular:
          </p>
  
          <h5 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#fff',
            marginTop: '16px',
            marginBottom: '12px',
          }}>1.1 Conclusion and performance of the contract</h5>
          <ul style={{
            paddingLeft: '24px',
            marginBottom: '16px',
          }}>
            <li style={{ marginBottom: '8px' }}>Verification of the conditions for concluding a contract;</li>
            <li style={{ marginBottom: '8px' }}>Performance of contractual obligations;</li>
            <li style={{ marginBottom: '8px' }}>Processing and responding to inquiries or complaints;</li>
            <li style={{ marginBottom: '8px' }}>Ensuring customer and user administration;</li>
            <li style={{ marginBottom: '8px' }}>Invoicing and, if necessary, the consultation of a debt collection agency.</li>
          </ul>
  
          <h5 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#fff',
            marginTop: '16px',
            marginBottom: '12px',
          }}>1.2 Data processing in our legitimate interest</h5>
          <ul style={{
            paddingLeft: '24px',
            marginBottom: '16px',
          }}>
            <li style={{ marginBottom: '8px' }}>Evaluation of your usage, ordering or inquiry behavior;</li>
            <li style={{ marginBottom: '8px' }}>Updating and validating your Data;</li>
            <li style={{ marginBottom: '8px' }}>Information about our services, products and promotions;</li>
            <li style={{ marginBottom: '8px' }}>Prevention of unlawful use of services;</li>
            <li style={{ marginBottom: '8px' }}>Maintenance, development and preservation of customer relations;</li>
            <li style={{ marginBottom: '8px' }}>Individualizing services and providing personalized content;</li>
            <li style={{ marginBottom: '8px' }}>Direct marketing and analysis purposes.</li>
          </ul>
  
          <p style={{ marginTop: '24px' }}>
            Last update: January 2024
          </p>
          
          <p style={{ marginTop: '24px', marginBottom: '16px' }}>
            Further legal information can be found at:  
            <a 
              href="https://www.sunrise.ch/en/general/legal-matters/data-protection" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: '#FF3B30',
                textDecoration: 'none',
                marginLeft: '6px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#FF5E54';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#FF3B30';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Sunrise Privacy Policy
            </a>
          </p>
        </div>
  
        {/* Modal Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button 
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
              border: 'none',
              borderRadius: '12px',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onClick={() => setShowPrivacyPolicy(false)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 59, 48, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );

  // Login view
  const renderLoginView = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 80px)',
      padding: '20px',
    }}>
      <div style={styles.formContainer}>
        <div style={styles.formHeader}>
          <div style={{
            ...styles.logoText,
            fontSize: '36px',
            textAlign: 'center',
            marginBottom: '10px',
          }}>cAmPP</div>
          <h1 style={styles.formTitle}>Welcome Back</h1>
          <p style={styles.formSubtitle}>Sign in to continue to your account</p>
        </div>

        <form onSubmit={handleLogin}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <Mail size={16} />
              Email Address
            </label>
            <input
              type="email"
              style={{
                ...styles.formInput,
                ...(inputFocus === 'login-email' && styles.formInputFocused)
              }}
              placeholder="your@email.com"
              value={loginData.email}
              onChange={(e) => setLoginData({...loginData, email: e.target.value})}
              onFocus={() => setInputFocus('login-email')}
              onBlur={() => setInputFocus(null)}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <Key size={16} />
              Password
            </label>
            <div style={styles.passwordInput}>
              <input
                type={showPassword ? "text" : "password"}
                style={{
                  ...styles.formInput,
                  ...(inputFocus === 'login-password' && styles.formInputFocused)
                }}
                placeholder="••••••••"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                onFocus={() => setInputFocus('login-password')}
                onBlur={() => setInputFocus(null)}
                required
              />
              <button 
                type="button" 
                style={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            style={{
              ...styles.button,
              ...styles.primaryButton,
              marginTop: '25px',
              ...(loading && styles.buttonDisabled)
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                Signing in...
              </>
            ) : (
              <>
                Sign In
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>

        <div style={styles.formDivider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>OR</span>
          <div style={styles.dividerLine}></div>
        </div>

        <button 
          type="button" 
          style={{
            ...styles.button,
            ...styles.secondaryButton,
          }}
          onClick={() => changeView('register')}
          disabled={loading}
        >
          Create a New Account
        </button>

        <div style={{
          marginTop: theme.spacing.xl,
          textAlign: 'center',
          fontSize: '14px',
          color: theme.colors.textSecondary,
        }}>
          For Sunrise Training Administrators Only
        </div>
      </div>
    </div>
  );

  // Register view
  const renderRegisterView = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 80px)',
      padding: '20px',
    }}>
      <div style={styles.formContainer}>
        <div style={styles.formHeader}>
          <div style={{
            ...styles.logoText,
            fontSize: '36px',
            textAlign: 'center',
            marginBottom: '10px',
          }}>cAmPP</div>
          <h1 style={styles.formTitle}>Create Account</h1>
          <p style={styles.formSubttitle}>Join us and enjoy your camp experience</p>
        </div>

        <form onSubmit={handleRegister}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <User size={16} />
              Username
            </label>
            <input
              type="text"
              style={{
                ...styles.formInput,
                ...(inputFocus === 'register-username' && styles.formInputFocused)
              }}
              placeholder="Your name"
              value={registerData.username}
              onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
              onFocus={() => setInputFocus('register-username')}
              onBlur={() => setInputFocus(null)}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <Mail size={16} />
              Email Address
            </label>
            <input
              type="email"
              style={{
                ...styles.formInput,
                ...(inputFocus === 'register-email' && styles.formInputFocused)
              }}
              placeholder="your@email.com"
              value={registerData.email}
              onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
              onFocus={() => setInputFocus('register-email')}
              onBlur={() => setInputFocus(null)}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <Key size={16} />
              Password
            </label>
            <div style={styles.passwordInput}>
              <input
                type={showPassword ? "text" : "password"}
                style={{
                  ...styles.formInput,
                  ...(inputFocus === 'register-password' && styles.formInputFocused)
                }}
                placeholder="••••••••"
                value={registerData.password}
                onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                onFocus={() => setInputFocus('register-password')}
                onBlur={() => setInputFocus(null)}
                required
              />
              <button 
                type="button" 
                style={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p style={styles.formHint}>Must be at least 6 characters</p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              <Key size={16} />
              Confirm Password
            </label>
            <div style={styles.passwordInput}>
              <input
                type={showPassword ? "text" : "password"}
                style={{
                  ...styles.formInput,
                  ...(inputFocus === 'register-confirm' && styles.formInputFocused)
                }}
                placeholder="••••••••"
                value={registerData.confirmPassword}
                onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                onFocus={() => setInputFocus('register-confirm')}
                onBlur={() => setInputFocus(null)}
                required
              />
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            margin: '20px 0',
            cursor: 'pointer',
            userSelect: 'none',
          }}
          onClick={() => setRegisterData({ ...registerData, tosAgreed: !registerData.tosAgreed })}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              border: `2px solid ${registerData.tosAgreed ? theme.colors.primary : theme.colors.dividerStrong}`,
              background: registerData.tosAgreed ? theme.colors.primary : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: theme.transitions.medium,
              flexShrink: 0,
            }}>
              {registerData.tosAgreed && <Check size={14} color="#fff" />}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              lineHeight: 1.5,
            }}>
              I agree to the{' '}
              <button
                type="button"
                style={styles.formLink}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTerms(true);
                }}
              >
                Terms of Service
              </button>
              {' '}and{' '}
              <button
                type="button"
                style={styles.formLink}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPrivacyPolicy(true);
                }}
              >
                Privacy Policy
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            style={{
              ...styles.button,
              ...styles.primaryButton,
              marginTop: '15px',
              ...(loading && styles.buttonDisabled)
            }}
            disabled={loading || !registerData.tosAgreed}
          >
            {loading ? (
              <>
                <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                Registering...
              </>
            ) : (
              <>
                Create Account
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>

        <div style={styles.formDivider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>OR</span>
          <div style={styles.dividerLine}></div>
        </div>

        <button 
          type="button" 
          style={{
            ...styles.button,
            ...styles.secondaryButton,
          }}
          onClick={() => changeView('login')}
          disabled={loading}
        >
          <ArrowLeft size={18} />
          Back to Login
        </button>
      </div>
    </div>
  );
  const renderRedSchedule = () => {
    // Group appointments by date
    const groupedAppointments = appointments.reduce((acc, appointment) => {
      const date = new Date(appointment.time);
      const dateString = date.toISOString().split('T')[0];
  
      if (!acc[dateString]) {
        acc[dateString] = [];
      }
  
      acc[dateString].push(appointment);
      return acc;
    }, {});
  
    // Sort dates
    const sortedDates = Object.keys(groupedAppointments).sort();
  
    return (
      <div style={{
        animation: 'fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        maxWidth: windowWidth < 992 ? '100%' : '900px',
        margin: '0 auto',
        width: '100%',
        padding: windowWidth < 576 ? '0 10px' : '0 20px',
      }}>
        <div style={{
          ...styles.card,
          background: darkMode 
            ? 'linear-gradient(to bottom right, rgba(31, 41, 55, 0.95), rgba(31, 41, 55, 0.85))'
            : 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
          position: 'relative',
          overflow: 'hidden',
          borderLeft: '5px solid #FF3B30',
        }}>
          <div style={{position: 'relative', zIndex: 1}}>
            <h1 style={{
              fontSize: windowWidth < 576 ? '24px' : '28px',
              fontWeight: 800,
              marginBottom: '30px',
              color: '#FF3B30',
              display: 'inline-block',
            }}>Dringende Termine</h1>
  
            {sortedDates.length > 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}>
                {sortedDates.map((dateString, dateIndex) => (
                  <div 
                    key={dateString} 
                    style={{
                      background: darkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.7)',
                      borderRadius: '18px',
                      border: '1px solid #FF3B30',
                      overflow: 'hidden',
                      animation: `fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${dateIndex * 0.1}s both`,
                      boxShadow: '0 4px 15px rgba(255, 59, 48, 0.15)',
                    }}
                  >
                    <div style={{
                      background: 'rgba(255, 59, 48, 0.1)',
                      padding: '18px 20px',
                      borderBottom: '1px solid rgba(255, 59, 48, 0.2)',
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        margin: 0,
                        color: '#FF3B30',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <Activity size={18} color="#FF3B30" />
                        {new Date(dateString).toLocaleDateString('de-DE', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long'
                        })}
                      </h3>
                    </div>
                    
                    <div style={{ padding: '15px' }}>
                      {groupedAppointments[dateString]
                        .filter(app => app.isUrgent)
                        .map((appointment, appIndex) => (
                          <div 
                            key={appointment.id} 
                            style={{
                              padding: '15px',
                              background: 'rgba(255, 59, 48, 0.05)',
                              borderRadius: '12px',
                              marginBottom: '10px',
                              borderLeft: '3px solid #FF3B30',
                              animation: `fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${(dateIndex * 0.1) + (appIndex * 0.05)}s both`,
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '10px',
                            }}>
                              <h4 style={{
                                fontSize: '16px',
                                fontWeight: 600,
                                margin: 0,
                                color: '#FF3B30',
                              }}>{appointment.label}</h4>
                              <div style={{
                                fontWeight: 600,
                                fontSize: '14px',
                                color: '#FF3B30',
                                padding: '4px 8px',
                                borderRadius: '8px',
                                background: 'rgba(255, 59, 48, 0.1)',
                              }}>
                                {formatTime(appointment.time)}
                              </div>
                            </div>
                            
                            {appointment.description && (
                              <p style={{
                                fontSize: '14px',
                                color: darkMode ? 'rgba(209, 213, 219, 0.8)' : 'rgba(75, 85, 99, 0.9)',
                                margin: 0,
                                lineHeight: 1.5,
                              }}>{appointment.description}</p>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px 0',
              }}>
                <p style={{
                  color: darkMode ? 'rgba(209, 213, 219, 0.7)' : 'rgba(107, 114, 128, 0.9)',
                }}>
                  Keine dringenden Termine vorhanden.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };const NotificationConfigurator = () => {
    const [notificationSettings, setNotificationSettings] = useState({
      email: true,
      push: true,
      schedule: true,
      messages: true
    });
  
    const handleToggle = (type) => {
      setNotificationSettings({
        ...notificationSettings,
        [type]: !notificationSettings[type]
      });
    };
  
    const saveNotificationSettings = async () => {
      try {
        setLoading(true);
        
        await api('/api/auth/notification-settings', 'PUT', notificationSettings);
        
        showSuccess('Benachrichtigungseinstellungen gespeichert');
        setLoading(false);
      } catch (err) {
        showError('Fehler beim Speichern der Einstellungen');
        setLoading(false);
      }
    };
  
    return (
      <div style={{
        ...styles.card,
        background: darkMode 
          ? 'linear-gradient(to bottom right, rgba(31, 41, 55, 0.95), rgba(31, 41, 55, 0.85))'
          : 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <h2 style={styles.sectionTitle}>
          <Bell size={20} style={{color: '#FF3B30'}} />
          Benachrichtigungseinstellungen
        </h2>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          marginBottom: '25px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '15px',
            background: darkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.7)',
            borderRadius: '12px',
            border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
          }}>
            <div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 600,
                margin: '0 0 5px 0',
                color: darkMode ? '#f9fafb' : '#1f2937',
              }}>E-Mail Benachrichtigungen</h3>
              <p style={{
                fontSize: '14px',
                color: darkMode ? 'rgba(209, 213, 219, 0.7)' : 'rgba(107, 114, 128, 0.9)',
                margin: 0,
              }}>Erhalte Benachrichtigungen per E-Mail</p>
            </div>
            <div 
              style={{
                width: '50px',
                height: '26px',
                borderRadius: '13px',
                background: notificationSettings.email ? '#10b981' : darkMode ? '#4b5563' : '#d1d5db',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onClick={() => handleToggle('email')}
            >
              <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '11px',
                background: '#fff',
                position: 'absolute',
                top: '2px',
                left: notificationSettings.email ? '26px' : '2px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
              }}></div>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '15px',
            background: darkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.7)',
            borderRadius: '12px',
            border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
          }}>
            <div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 600,
                margin: '0 0 5px 0',
                color: darkMode ? '#f9fafb' : '#1f2937',
              }}>Push-Benachrichtigungen</h3>
              <p style={{
                fontSize: '14px',
                color: darkMode ? 'rgba(209, 213, 219, 0.7)' : 'rgba(107, 114, 128, 0.9)',
                margin: 0,
              }}>Erhalte Push-Benachrichtigungen auf deinem Gerät</p>
            </div>
            <div 
              style={{
                width: '50px',
                height: '26px',
                borderRadius: '13px',
                background: notificationSettings.push ? '#10b981' : darkMode ? '#4b5563' : '#d1d5db',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onClick={() => handleToggle('push')}
            >
              <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '11px',
                background: '#fff',
                position: 'absolute',
                top: '2px',
                left: notificationSettings.push ? '26px' : '2px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
              }}></div>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '15px',
            background: darkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.7)',
            borderRadius: '12px',
            border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
          }}>
            <div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 600,
                margin: '0 0 5px 0',
                color: darkMode ? '#f9fafb' : '#1f2937',
              }}>Terminänderungen</h3>
              <p style={{
                fontSize: '14px',
                color: darkMode ? 'rgba(209, 213, 219, 0.7)' : 'rgba(107, 114, 128, 0.9)',
                margin: 0,
              }}>Erhalte Benachrichtigungen bei Terminänderungen</p>
            </div>
            <div 
              style={{
                width: '50px',
                height: '26px',
                borderRadius: '13px',
                background: notificationSettings.schedule ? '#10b981' : darkMode ? '#4b5563' : '#d1d5db',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onClick={() => handleToggle('schedule')}
            >
              <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '11px',
                background: '#fff',
                position: 'absolute',
                top: '2px',
                left: notificationSettings.schedule ? '26px' : '2px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
              }}></div>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '15px',
            background: darkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.7)',
            borderRadius: '12px',
            border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
          }}>
            <div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 600,
                margin: '0 0 5px 0',
                color: darkMode ? '#f9fafb' : '#1f2937',
              }}>Nachrichten</h3>
              <p style={{
                fontSize: '14px',
                color: darkMode ? 'rgba(209, 213, 219, 0.7)' : 'rgba(107, 114, 128, 0.9)',
                margin: 0,
              }}>Erhalte Benachrichtigungen bei neuen Nachrichten</p>
            </div>
            <div 
              style={{
                width: '50px',
                height: '26px',
                borderRadius: '13px',
                background: notificationSettings.messages ? '#10b981' : darkMode ? '#4b5563' : '#d1d5db',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onClick={() => handleToggle('messages')}
            >
              <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '11px',
                background: '#fff',
                position: 'absolute',
                top: '2px',
                left: notificationSettings.messages ? '26px' : '2px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
              }}></div>
            </div>
          </div>
        </div>
        
        <button 
          style={{
            ...styles.primaryButton,
            background: 'linear-gradient(145deg, #FF3B30, #FF2D55)',
          }}
          onClick={saveNotificationSettings}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 59, 48, 0.4), 0 2px 5px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 59, 48, 0.3), 0 1px 2px rgba(0, 0, 0, 0.1)';
          }}
        >
          {loading ? (
            <>
              <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
              Speichern...
            </>
          ) : (
            <>
              Einstellungen speichern
              <Save size={18} />
            </>
          )}
        </button>
      </div>
    );
  };
  // Dashboard view
  const renderDashboardView = () => (
    <div style={styles.dashboardContainer}>
      <div style={styles.welcomeBanner}>
        {/* Background effect */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '100%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(255,59,48,0.1) 0%, transparent 70%)',
          filter: 'blur(50px)',
          transform: 'rotate(-15deg)',
          opacity: 0.7,
          zIndex: 0,
        }} />
        
        <div style={{
          position: 'absolute',
          bottom: '-50%',
          left: '-10%',
          width: '70%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(255,45,85,0.1) 0%, transparent 70%)',
          filter: 'blur(50px)',
          transform: 'rotate(15deg)',
          opacity: 0.7,
          zIndex: 0,
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={styles.welcomeTitle}>Welcome, {userData?.username || 'User'}!</h1>
          <p style={styles.welcomeText}>
            Here you'll find an overview of your account and current information about the camp.
          </p>
        </div>
      </div>

      {activeCamp ? (
        <div style={{
          ...styles.card,
          position: 'relative',
          overflow: 'hidden',
          animation: 'scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both',
        }}>
          {/* Background gradient */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.05), rgba(255, 45, 85, 0.02))',
            zIndex: 0,
          }} />
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>
                <Calendar size={22} style={{ color: theme.colors.primary }} />
                Active Camp
              </h2>
            </div>

            <div style={{
              background: darkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.7)',
              borderRadius: '20px',
              padding: '24px',
              display: 'flex',
              flexDirection: windowWidth < 768 ? 'column' : 'row',
              alignItems: windowWidth < 768 ? 'flex-start' : 'center',
              justifyContent: 'space-between',
              gap: '20px',
              border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.8)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.05), rgba(255, 45, 85, 0.02))',
                zIndex: 0,
              }} />
              
              <div style={{
                flex: 1,
                position: 'relative',
                zIndex: 1,
              }}>
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  margin: '0 0 8px 0',
                  color: darkMode ? '#f9fafb' : '#1f2937',
                }}>{activeCamp.title}</h3>
                
                <div style={{
                  fontSize: '15px',
                  color: darkMode ? 'rgba(209, 213, 219, 0.8)' : 'rgba(75, 85, 99, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: '0 0 15px 0',
                }}>
                  <Calendar size={16} style={{ opacity: 0.7 }} />
                  <span>
                    {new Date(activeCamp.startDate).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })} - 
                    {new Date(activeCamp.endDate).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                
                {activeCamp.currentPhase && (
                  <div style={{
                    marginTop: '15px',
                    display: 'inline-block',
                  }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: 'linear-gradient(to right, rgba(255, 59, 48, 0.15), rgba(255, 45, 85, 0.15))',
                      color: '#FF3B30',
                      fontSize: '13px',
                      fontWeight: 600,
                      padding: '8px 14px',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(255, 59, 48, 0.15)',
                      border: '1px solid rgba(255, 59, 48, 0.2)',
                      gap: '8px',
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#FF3B30',
                        boxShadow: '0 0 0 2px rgba(255, 59, 48, 0.3)',
                        animation: 'pulse 2s infinite',
                      }}></div>
                      {activeCamp.currentPhase.name || (
                        activeCamp.currentPhase.phaseType === 'PRE_CAMP' ? 'Pre-Camp' :
                        activeCamp.currentPhase.phaseType === 'MAIN_CAMP' ? 'Main Camp' :
                        activeCamp.currentPhase.phaseType === 'POST_CAMP' ? 'Post-Camp' : 
                        'Active Phase'
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <button 
                style={{
                  background: 'linear-gradient(to right, rgba(255, 59, 48, 0.1), rgba(255, 45, 85, 0.1))',
                  color: '#FF3B30',
                  border: '1px solid rgba(255, 59, 48, 0.2)',
                  padding: '12px 18px',
                  borderRadius: '14px',
                  fontSize: '15px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  width: windowWidth < 768 ? '100%' : 'auto',
                  justifyContent: windowWidth < 768 ? 'center' : 'flex-start',
                  boxShadow: '0 4px 10px rgba(255, 59, 48, 0.1)',
                  position: 'relative',
                  zIndex: 1,
                }}
                onClick={() => {
                  if (activeCamp.currentPhase?.phaseType === 'PRE_CAMP') {
                    changeView('pre-camp');
                  } else if (activeCamp.currentPhase?.phaseType === 'POST_CAMP') {
                    changeView('post-camp');
                  } else {
                    changeView('calendar');
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 15px rgba(255, 59, 48, 0.15)';
                  e.currentTarget.style.background = 'linear-gradient(to right, rgba(255, 59, 48, 0.15), rgba(255, 45, 85, 0.15))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(255, 59, 48, 0.1)';
                  e.currentTarget.style.background = 'linear-gradient(to right, rgba(255, 59, 48, 0.1), rgba(255, 45, 85, 0.1))';
                }}
              >
                View Details <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          ...styles.card,
          textAlign: 'center',
          padding: '40px 20px',
          animation: 'scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both',
        }}>
          <div style={{
            color: theme.colors.textTertiary,
            marginBottom: '20px',
            animation: 'pulse 2s infinite',
            display: 'flex',
            justifyContent: 'center',
          }}>
            <Calendar size={64} />
          </div>
          <h3 style={{
            fontSize: '22px',
            fontWeight: 700,
            marginBottom: '15px',
            color: theme.colors.textPrimary,
          }}>No Active Camp</h3>
          <p style={{
            color: theme.colors.textSecondary,
            maxWidth: '450px',
            margin: '0 auto',
            lineHeight: 1.6,
          }}>
            There is currently no active camp. Your schedule information will appear here once a camp begins.
          </p>
        </div>
      )}

      {/* Active or Next Appointment Card - Only shown if in MAIN_CAMP phase */}
      {activeCamp && activeCamp.currentPhase?.phaseType === 'MAIN_CAMP' && (activeAppointment || nextAppointment) && (
        <div style={{
          ...styles.card,
          animation: 'scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s both',
        }}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>
              <Activity size={22} style={{ color: activeAppointment ? theme.colors.primary : '#3b82f6' }} />
              {activeAppointment ? 'Current Activity' : 'Next Activity'}
            </h2>
          </div>
          
          <div style={{
            ...(activeAppointment ? styles.appointmentCardActive : styles.appointmentCardNext),
            padding: windowWidth < 576 ? '16px' : '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '16px',
          }}>
            <div style={{
              flex: 1,
              minWidth: 0,
              paddingRight: '15px',
            }}>
              <h3 style={{
                fontSize: '22px',
                fontWeight: 700,
                margin: '0 0 10px 0',
                color: activeAppointment ? theme.colors.primary : darkMode ? '#f9fafb' : '#1f2937',
              }}>
                {(activeAppointment || nextAppointment).label}
              </h3>
              
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                marginBottom: '12px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  color: darkMode ? 'rgba(209, 213, 219, 0.8)' : 'rgba(75, 85, 99, 0.9)',
                  padding: '6px 12px',
                  background: darkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                  borderRadius: '8px',
                  border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
                }}>
                  <Activity size={16} />
                  {formatTimeSpan(
                    (activeAppointment || nextAppointment).time,
                    (activeAppointment || nextAppointment).duration
                  )}
                </div>
                
                {(activeAppointment || nextAppointment).location && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: darkMode ? 'rgba(209, 213, 219, 0.8)' : 'rgba(75, 85, 99, 0.9)',
                    padding: '6px 12px',
                    background: darkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                    borderRadius: '8px',
                    border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
                  }}>
                    <Activity size={16} />
                    {(activeAppointment || nextAppointment).location}
                  </div>
                )}
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: activeAppointment ? theme.colors.primary : '#3b82f6',
                  padding: '6px 12px',
                  background: activeAppointment 
                    ? 'rgba(255, 59, 48, 0.1)'
                    : 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '8px',
                  border: activeAppointment
                    ? '1px solid rgba(255, 59, 48, 0.2)'
                    : '1px solid rgba(59, 130, 246, 0.2)',
                  animation: 'pulse 2s infinite',
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: activeAppointment ? theme.colors.primary : '#3b82f6',
                    boxShadow: activeAppointment
                      ? '0 0 0 2px rgba(255, 59, 48, 0.3)'
                      : '0 0 0 2px rgba(59, 130, 246, 0.3)',
                  }}></div>
                  {timeLeft}
                </div>
              </div>
              
              {(activeAppointment || nextAppointment).description && (
                <p style={{
                  color: darkMode ? 'rgba(209, 213, 219, 0.9)' : 'rgba(75, 85, 99, 0.9)',
                  fontSize: '15px',
                  margin: '0',
                  lineHeight: 1.6,
                  padding: '12px 16px',
                  background: darkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                  borderRadius: '10px',
                  border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
                }}>
                  {(activeAppointment || nextAppointment).description}
                </p>
              )}
            </div>
            
            <div style={{
              width: '60px',
              height: '60px',
              flexShrink: 0,
              borderRadius: '16px',
              background: activeAppointment 
                ? 'linear-gradient(135deg, rgba(255, 59, 48, 0.2), rgba(255, 45, 85, 0.2))'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.2))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: activeAppointment ? theme.colors.primary : '#3b82f6',
              boxShadow: activeAppointment
                ? '0 4px 12px rgba(255, 59, 48, 0.2)'
                : '0 4px 12px rgba(59, 130, 246, 0.2)',
              border: activeAppointment
                ? '1px solid rgba(255, 59, 48, 0.3)'
                : '1px solid rgba(59, 130, 246, 0.3)',
            }}>
              {getActivityIcon((activeAppointment || nextAppointment).label)}
            </div>
          </div>
          
          <button
            style={{
              ...styles.secondaryButton,
              marginTop: '15px',
              background: 'transparent',
              border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            }}
            onClick={() => changeView('calendar')}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            View Full Schedule <Calendar size={18} />
          </button>
        </div>
      )}
    {/* Quick access section */}
    <div style={{
        ...styles.card,
        animation: 'scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.7s both',
      }}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>
            <Activity size={22} style={{ color: theme.colors.primary }} />
            Quick Access
          </h2>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '15px',
        }}>
          <div 
            style={{
              padding: '20px',
              borderRadius: theme.borderRadius.large,
              background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
              border: `1px solid ${theme.colors.divider}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '15px',
              cursor: 'pointer',
              transition: theme.transitions.medium,
            }}
            onClick={() => changeView('profile')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = theme.shadows.medium;
              e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)';
            }}
          >
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: theme.borderRadius.medium,
              background: `linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.secondary}20)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.colors.primary,
            }}>
              <User size={24} />
            </div>
            <span style={{
              fontSize: '15px',
              fontWeight: '600',
              color: theme.colors.textPrimary,
              textAlign: 'center',
            }}>My Profile</span>
          </div>
          
          {activeCamp && (
            <div 
              style={{
                padding: '20px',
                borderRadius: theme.borderRadius.large,
                background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
                border: `1px solid ${theme.colors.divider}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '15px',
                cursor: 'pointer',
                transition: theme.transitions.medium,
              }}
              onClick={() => {
                if (activeCamp.currentPhase?.phaseType === 'PRE_CAMP') {
                  changeView('pre-camp');
                } else if (activeCamp.currentPhase?.phaseType === 'POST_CAMP') {
                  changeView('post-camp');
                } else {
                  changeView('calendar');
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = theme.shadows.medium;
                e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)';
              }}
            >
              <div style={{
                width: '50px',
                height: '50px',
                borderRadius: theme.borderRadius.medium,
                background: `linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.secondary}20)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.primary,
              }}>
                <Calendar size={24} />
              </div>
              <span style={{
                fontSize: '15px',
                fontWeight: '600',
                color: theme.colors.textPrimary,
                textAlign: 'center',
              }}>
                {activeCamp.currentPhase?.phaseType === 'PRE_CAMP' 
                  ? 'Pre-Camp' 
                  : activeCamp.currentPhase?.phaseType === 'POST_CAMP'
                    ? 'Post-Camp'
                    : 'Schedule'}
              </span>
            </div>
          )}
          
          <div 
            style={{
              padding: '20px',
              borderRadius: theme.borderRadius.large,
              background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
              border: `1px solid ${theme.colors.divider}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '15px',
              cursor: 'pointer',
              transition: theme.transitions.medium,
            }}
            onClick={() => changeView('edit')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = theme.shadows.medium;
              e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)';
            }}
          >
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: theme.borderRadius.medium,
              background: `linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.secondary}20)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.colors.primary,
            }}>
              <Edit3 size={24} />
            </div>
            <span style={{
              fontSize: '15px',
              fontWeight: '600',
              color: theme.colors.textPrimary,
              textAlign: 'center',
            }}>Edit Profile</span>
          </div>
          
          <div 
            style={{
              padding: '20px',
              borderRadius: theme.borderRadius.large,
              background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
              border: `1px solid ${theme.colors.divider}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '15px',
              cursor: 'pointer',
              transition: theme.transitions.medium,
            }}
            onClick={handleLogout}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = theme.shadows.medium;
              e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)';
            }}
          >
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: theme.borderRadius.medium,
              background: `linear-gradient(135deg, ${theme.colors.error}20, ${theme.colors.error}10)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.colors.error,
            }}>
              <LogOut size={24} />
            </div>
            <span style={{
              fontSize: '15px',
              fontWeight: '600',
              color: theme.colors.textPrimary,
              textAlign: 'center',
            }}>Sign Out</span>
          </div>
        </div>
      </div>
      {/* Notifications section */}
      <div style={{
        ...styles.card,
        animation: 'scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s both',
      }}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>
            <Bell size={22} style={{ color: theme.colors.primary }} />
            Notifications
            {unreadNotificationCount > 0 && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: theme.colors.primary,
                color: '#fff',
                fontSize: '12px',
                fontWeight: '600',
                marginLeft: '8px',
              }}>
                {unreadNotificationCount}
              </span>
            )}
          </h2>
        </div>
        
        {notifications.length > 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            {notifications.slice(0, 3).map(notification => (
              <div 
                key={notification.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '15px',
                  padding: '20px',
                  background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
                  borderRadius: theme.borderRadius.medium,
                  cursor: 'pointer',
                  transition: theme.transitions.medium,
                  position: 'relative',
                  opacity: notification.isRead ? 0.7 : 1,
                }}
                onClick={() => markNotificationAsRead(notification.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = theme.shadows.medium;
                  e.currentTarget.style.borderColor = theme.colors.dividerStrong;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = theme.colors.divider;
                }}
              >
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: theme.borderRadius.medium,
                  background: `linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.secondary}20)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.colors.primary,
                  flexShrink: 0,
                }}>
                  {notification.type === 'system' ? <Info size={24} /> : <Bell size={24} />}
                </div>
                
                <div style={{
                  flex: 1,
                  minWidth: 0,
                }}>
                  <p style={{
                    margin: '0 0 8px 0',
                    lineHeight: 1.5,
                    color: theme.colors.textPrimary,
                    fontWeight: notification.isRead ? 400 : 500,
                  }}>{notification.message}</p>
                  
                  <div style={{
                    fontSize: '13px',
                    color: theme.colors.textTertiary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <Activity size={14} style={{ opacity: 0.7 }} />
                    {formatDate(notification.timestamp)}
                  </div>
                </div>
                
                {!notification.isRead && (
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: theme.colors.primary,
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    boxShadow: '0 0 0 3px rgba(255, 59, 48, 0.2)',
                    animation: 'pulse 2s infinite',
                  }}></div>
                )}
              </div>
            ))}
            
            {notifications.length > 3 && (
              <button 
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  marginTop: '15px',
                }}
                onClick={() => changeView('notifications')}
              >
                View All Notifications
              </button>
            )}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '30px 0',
          }}>
            <div style={{
              color: theme.colors.textTertiary,
              marginBottom: '15px',
              display: 'flex',
              justifyContent: 'center',
            }}>
              <Bell size={48} />
            </div>
            <p style={{
              color: theme.colors.textSecondary,
              margin: 0,
            }}>
              You have no new notifications
            </p>
          </div>
        )}
      </div>

  
    </div>
  );


  // Calendar view
  const renderCalendarView = () => {
    // Group appointments by date
    const groupedAppointments = appointments.reduce((acc, appointment) => {
      const date = new Date(appointment.time);
      const dateString = date.toISOString().split('T')[0];
  
      if (!acc[dateString]) {
        acc[dateString] = [];
      }
  
      acc[dateString].push(appointment);
      return acc;
    }, {});
  
    // Sort dates
    const sortedDates = Object.keys(groupedAppointments).sort();
  
    return (
      <div style={{
        animation: 'fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        maxWidth: windowWidth < 992 ? '100%' : '1200px',
        margin: '0 auto',
        width: '100%',
        padding: windowWidth < 576 ? '0 10px' : '0 20px',
      }}>
        <div style={{
          ...styles.card,
          background: darkMode 
            ? 'linear-gradient(to bottom right, rgba(31, 41, 55, 0.95), rgba(31, 41, 55, 0.85))'
            : 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
          position: 'relative',
          overflow: 'hidden',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-5px)';
          e.currentTarget.style.boxShadow = darkMode 
            ? '0 15px 35px rgba(0, 0, 0, 0.4)'
            : '0 15px 35px rgba(0, 0, 0, 0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = darkMode 
            ? '0 8px 25px rgba(0, 0, 0, 0.3)'
            : '0 8px 25px rgba(0, 0, 0, 0.08)';
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0.5,
            background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.08), rgba(255, 59, 48, 0.03))',
            zIndex: 0,
          }}></div>
          
          <div style={{position: 'relative', zIndex: 1}}>
            <h1 style={{
              fontSize: windowWidth < 576 ? '24px' : '28px',
              fontWeight: 800,
              marginBottom: '30px',
              background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
              textShadow: '0 2px 10px rgba(255, 59, 48, 0.2)',
            }}>Schedule</h1>
  
            {activeCamp && activeCamp.currentPhase?.phaseType === 'MAIN_CAMP' ? (
              <>
                <div style={styles.campHeader}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.05), rgba(255, 45, 85, 0.02))',
                    zIndex: 0,
                  }}></div>
                  
                  <div style={{position: 'relative', zIndex: 1}}>
                    <h2 style={{
                      fontSize: windowWidth < 576 ? '20px' : '24px',
                      fontWeight: 700,
                      margin: '0 0 10px 0',
                      color: darkMode ? '#f9fafb' : '#1f2937',
                    }}>{activeCamp.title}</h2>
                    <p style={{
                      fontSize: '16px',
                      color: darkMode ? 'rgba(209, 213, 219, 0.8)' : 'rgba(75, 85, 99, 0.9)',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                      <Calendar size={16} style={{opacity: 0.7}} />
                      {new Date(activeCamp.startDate).toLocaleDateString('en-US', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })} - 
                      {new Date(activeCamp.endDate).toLocaleDateString('en-US', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
  
                {Object.keys(groupedAppointments).length > 0 ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px'
                  }}>
                    {sortedDates.map((dateString, dateIndex) => (
                      <div 
                        key={dateString} 
                        style={{
                          background: darkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.7)',
                          borderRadius: '18px',
                          border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
                          overflow: 'hidden',
                          animation: `fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${dateIndex * 0.1}s both`,
                        }}
                      >
                        <div style={{
                          background: darkMode 
                            ? 'linear-gradient(to right, rgba(31, 41, 55, 0.8), rgba(55, 65, 81, 0.8))'
                            : 'linear-gradient(to right, rgba(249, 250, 251, 0.8), rgba(243, 244, 246, 0.8))',
                          padding: '18px 20px',
                          borderBottom: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
                          position: 'relative',
                        }}>
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '5px',
                            height: '100%',
                            background: 'linear-gradient(to bottom, #FF3B30, #FF2D55)',
                          }}></div>
                          
                          <h3 style={{
                            fontSize: '18px',
                            fontWeight: 700,
                            margin: 0,
                            color: darkMode ? '#f9fafb' : '#1f2937',
                            paddingLeft: '15px',
                          }}>
                            {new Date(dateString).toLocaleDateString('en-US', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long'
                            })}
                          </h3>
                        </div>
                        
                        <div style={{
                          padding: windowWidth < 576 ? '15px' : '20px'
                        }}>
                          {groupedAppointments[dateString].map((appointment, appIndex) => {
                            const now = new Date();
                            const appTime = new Date(appointment.time);
                            const endTime = new Date(appTime.getTime() + (appointment.duration || 60) * 60000);
                            const isActive = now >= appTime && now <= endTime;
                            const isPast = now > endTime;
                            
                            return (
                              <div 
                                key={appointment.id} 
                                style={{
                                  ...styles.appointmentCard,
                                  borderLeft: isActive 
                                    ? '4px solid #FF3B30' 
                                    : isPast 
                                      ? '4px solid #9CA3AF' 
                                      : '4px solid #3b82f6',
                                  marginBottom: '14px',
                                  opacity: isPast ? 0.7 : 1,
                                  padding: windowWidth < 576 ? '14px' : '18px',
                                  animation: `fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${(dateIndex * 0.1) + (appIndex * 0.05)}s both`,
                                  background: isActive
                                    ? darkMode 
                                      ? 'linear-gradient(135deg, rgba(255, 59, 48, 0.2), rgba(255, 45, 85, 0.15))'
                                      : 'linear-gradient(135deg, rgba(255, 59, 48, 0.1), rgba(255, 45, 85, 0.08))'
                                    : isPast
                                      ? darkMode 
                                        ? 'rgba(75, 85, 99, 0.3)'
                                        : 'rgba(229, 231, 235, 0.5)'
                                      : darkMode 
                                        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.15))'
                                        : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.08))',
                                }}
                              >
                                <div style={{
                                  minWidth: '65px',
                                  fontWeight: 600,
                                  fontSize: '16px',
                                  color: isActive 
                                    ? '#FF3B30' 
                                    : isPast
                                      ? darkMode 
                                        ? '#9ca3af' 
                                        : '#6b7280'
                                      : '#3b82f6',
                                  marginRight: '15px',
                                  padding: '6px 12px',
                                  borderRadius: '10px',
                                  background: isActive
                                    ? 'rgba(255, 59, 48, 0.1)'
                                    : isPast
                                      ? 'rgba(156, 163, 175, 0.1)'
                                      : 'rgba(59, 130, 246, 0.1)',
                                  textAlign: 'center',
                                  border: isActive
                                    ? '1px solid rgba(255, 59, 48, 0.2)'
                                    : isPast
                                      ? '1px solid rgba(156, 163, 175, 0.2)'
                                      : '1px solid rgba(59, 130, 246, 0.2)',
                                  boxShadow: isActive
                                    ? '0 2px 5px rgba(255, 59, 48, 0.15)'
                                    : isPast
                                      ? 'none'
                                      : '0 2px 5px rgba(59, 130, 246, 0.15)',
                                }}>
                                  {formatTime(appointment.time)}
                                </div>
                                <div style={{
                                  flex: 1
                                }}>
                                  <h4 style={{
                                    flex: 1,
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    color: isActive 
                                      ? '#FF3B30' 
                                      : isPast
                                        ? darkMode 
                                          ? '#9ca3af' 
                                          : '#6b7280'
                                        : darkMode 
                                          ? '#f9fafb' 
                                          : '#1f2937',
                                    margin: '0 0 10px 0'
                                  }}>{appointment.label}</h4>
                                  <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '12px',
                                    marginBottom: '10px'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      color: darkMode ? 'rgba(209, 213, 219, 0.8)' : 'rgba(75, 85, 99, 0.9)',
                                      fontSize: '14px',
                                      padding: '4px 10px',
                                      borderRadius: '8px',
                                      background: darkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                                      border: darkMode ? '1px solid rgba(75, 85, 99, 0.2)' : '1px solid rgba(229, 231, 235, 0.5)',
                                    }}>
                                      <Activity size={14} />
                                      <span>{formatTimeSpan(appointment.time, appointment.duration)}</span>
                                    </div>
                                    {appointment.location && (
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        color: darkMode ? 'rgba(209, 213, 219, 0.8)' : 'rgba(75, 85, 99, 0.9)',
                                        fontSize: '14px',
                                        padding: '4px 10px',
                                        borderRadius: '8px',
                                        background: darkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                                        border: darkMode ? '1px solid rgba(75, 85, 99, 0.2)' : '1px solid rgba(229, 231, 235, 0.5)',
                                      }}>
                                        <Activity size={14} />
                                        <span>{appointment.location}</span>
                                      </div>
                                    )}
                                  </div>
                                  {appointment.description && (
                                    <p style={{
                                      fontSize: '14px',
                                      color: darkMode ? 'rgba(209, 213, 219, 0.8)' : 'rgba(75, 85, 99, 0.9)',
                                      margin: 0,
                                      lineHeight: 1.5,
                                      padding: '8px 12px',
                                      borderRadius: '10px',
                                      background: darkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                                      border: darkMode ? '1px solid rgba(75, 85, 99, 0.2)' : '1px solid rgba(229, 231, 235, 0.5)',
                                    }}>{appointment.description}</p>
                                  )}
                                </div>
                                <div style={{
                                  marginLeft: windowWidth < 576 ? '0' : '15px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  order: windowWidth < 576 ? -1 : 0,
                                  marginRight: windowWidth < 576 ? '15px' : '0'
                                }}>
                                  <div style={{
                                    width: '50px',
                                    height: '50px',
                                    borderRadius: '14px',
                                    background: isActive
                                      ? 'linear-gradient(135deg, rgba(255, 59, 48, 0.3), rgba(255, 45, 85, 0.3))'
                                      : isPast
                                        ? 'linear-gradient(135deg, rgba(156, 163, 175, 0.3), rgba(107, 114, 128, 0.3))'
                                        : 'linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(37, 99, 235, 0.3))',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: isActive 
                                      ? '#FF3B30' 
                                      : isPast
                                        ? '#9CA3AF'
                                        : '#3b82f6',
                                    boxShadow: isActive
                                      ? '0 4px 10px rgba(255, 59, 48, 0.2)'
                                      : isPast
                                        ? 'none'
                                        : '0 4px 10px rgba(59, 130, 246, 0.2)',
                                    border: isActive
                                      ? '1px solid rgba(255, 59, 48, 0.3)'
                                      : isPast
                                        ? '1px solid rgba(156, 163, 175, 0.3)'
                                        : '1px solid rgba(59, 130, 246, 0.3)',
                                  }}>
                                    {getActivityIcon(appointment.label)}
                                  </div>
                                </div>
                                {isActive && (
                                  <div style={{
                                    background: 'rgba(255, 59, 48, 0.1)',
                                    borderRadius: '10px',
                                    padding: '8px 12px',
                                    marginTop: '12px',
                                    marginLeft: windowWidth < 576 ? '0' : '15px',
                                    width: windowWidth < 576 ? '100%' : 'auto',
                                    textAlign: windowWidth < 576 ? 'center' : 'left',
                                    border: '1px solid rgba(255, 59, 48, 0.2)',
                                    boxShadow: '0 2px 5px rgba(255, 59, 48, 0.15)',
                                    animation: 'pulse 2s infinite',
                                  }}>
                                    <span style={{
                                      color: '#FF3B30',
                                      fontSize: '14px',
                                      fontWeight: 600,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      justifyContent: windowWidth < 576 ? 'center' : 'flex-start',
                                    }}>
                                      <div style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        background: '#FF3B30',
                                        boxShadow: '0 0 0 2px rgba(255, 59, 48, 0.3)',
                                        animation: 'pulse 2s infinite',
                                      }}></div>
                                      Now Active
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    background: darkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.7)',
                    borderRadius: '16px',
                    padding: '50px 20px',
                    alignItems: 'center',
                    marginTop: '15px',
                    textAlign: 'center',
                    border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
                    animation: 'fadeIn 0.5s ease-in-out',
                  }}>
                    <div style={{
                      marginBottom: '20px',
                      color: darkMode ? 'rgba(107, 114, 128, 0.7)' : 'rgba(156, 163, 175, 0.8)',
                      animation: 'pulse 2s infinite ease-in-out',
                    }}>
                      <Calendar size={64} />
                    </div>
                    <h3 style={{
                      fontSize: '22px',
                      fontWeight: 700,
                      marginBottom: '10px',
                      color: darkMode ? '#f9fafb' : '#1f2937'
                    }}>No events found</h3>
                    <p style={{
                      fontSize: '16px',
                      color: darkMode ? 'rgba(209, 213, 219, 0.7)' : 'rgba(107, 114, 128, 0.9)',
                      textAlign: 'center',
                      maxWidth: '400px',
                      margin: '0 auto',
                    }}>
                      There are currently no events scheduled for this camp.
                    </p>
                  </div>
                )}
              </>
            ) : activeCamp && activeCamp.currentPhase?.phaseType === 'PRE_CAMP' ? (
              <div style={{
                animation: 'fadeIn 0.5s ease',
              }}>
                {handlePreCampView()}
              </div>
            ) : activeCamp && activeCamp.currentPhase?.phaseType === 'POST_CAMP' ? (
              <div style={{
                animation: 'fadeIn 0.5s ease',
              }}>
                {handlePostCampView()}
              </div>
            ) : (
              <div style={{
                background: darkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.7)',
                borderRadius: '16px',
                padding: '50px 20px',
                alignItems: 'center',
                marginTop: '15px',
                textAlign: 'center',
                border: darkMode ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(229, 231, 235, 0.5)',
                animation: 'fadeIn 0.5s ease-in-out',
              }}>
                <div style={{
                  marginBottom: '20px',
                  color: darkMode ? 'rgba(107, 114, 128, 0.7)' : 'rgba(156, 163, 175, 0.8)',
                  animation: 'pulse 2s infinite ease-in-out',
                }}>
                  <Calendar size={64} />
                </div>
                <h3 style={{
                  fontSize: '22px',
                  fontWeight: 700,
                  marginBottom: '10px',
                  color: darkMode ? '#f9fafb' : '#1f2937'
                }}>No Active Camp</h3>
                <p style={{
                  color: darkMode ? 'rgba(209, 213, 219, 0.7)' : 'rgba(107, 114, 128, 0.9)',
                  maxWidth: '400px',
                  margin: '0 auto',
                }}>
                  There is currently no active camp. Schedule information will appear here once a camp begins.
                </p>
              </div>
            )}
  
            <button 
              style={{
                ...styles.primaryButton,
                marginTop: '25px',
                background: 'linear-gradient(145deg, #FF3B30, #FF2D55)',
              }}
              onClick={() => changeView('dashboard')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 59, 48, 0.4), 0 2px 5px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 59, 48, 0.3), 0 1px 2px rgba(0, 0, 0, 0.1)';
              }}
            >
              <ArrowLeft size={18} /> Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Pre-Camp view
  const renderPreCampView = () => (
    <div style={{
      animation: 'fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
      maxWidth: windowWidth < 992 ? '100%' : '900px',
      margin: '0 auto',
      width: '100%',
      padding: windowWidth < 576 ? '0 10px' : '0 20px',
    }}>
      <div style={{
        ...styles.card,
        background: darkMode 
          ? 'linear-gradient(to bottom right, rgba(31, 41, 55, 0.95), rgba(31, 41, 55, 0.85))'
          : 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = darkMode 
          ? '0 15px 35px rgba(0, 0, 0, 0.4)'
          : '0 15px 35px rgba(0, 0, 0, 0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = darkMode 
          ? '0 8px 25px rgba(0, 0, 0, 0.3)'
          : '0 8px 25px rgba(0, 0, 0, 0.08)';
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.5,
          background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.08), rgba(255, 59, 48, 0.03))',
          zIndex: 0,
        }}></div>
        
        <div style={{position: 'relative', zIndex: 1}}>
          {handlePreCampView()}

          <button 
            style={{
              ...styles.primaryButton,
              marginTop: '25px',
              background: 'linear-gradient(145deg, #FF3B30, #FF2D55)',
            }}
            onClick={() => changeView('dashboard')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 59, 48, 0.4), 0 2px 5px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 59, 48, 0.3), 0 1px 2px rgba(0, 0, 0, 0.1)';
            }}
          >
            <ArrowLeft size={18} /> Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
  const renderProfileView = () => (
    <div style={styles.dashboardContainer}>
      <div style={{
        ...styles.card,
        padding: windowWidth < 576 ? '20px' : '30px',
      }}>
        <div style={styles.profileHeader}>
          <div style={styles.profileImageContainer}>
            {imageLoading ? (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: darkMode ? '#2D2D2D' : '#F2F2F2',
                borderRadius: theme.borderRadius.circle,
                animation: 'pulse 1.5s infinite ease-in-out',
              }}>
                <Loader size={30} color={theme.colors.textTertiary} />
              </div>
            ) : profileImage && !profileImageError ? (
              <img 
                src={profileImage} 
                alt={userData?.username} 
                style={styles.profileLargeImage}
                onError={() => setProfileImageError(true)}
              />
            ) : (
              <div 
                style={{
                  ...styles.profileLargeInitials,
                  backgroundColor: generateProfileColor(userData?.username),
                  color: useLightText(generateProfileColor(userData?.username)) ? '#FFFFFF' : '#000000',
                }}
              >
                {getInitials(userData?.username)}
              </div>
            )}
            
            <button 
              style={styles.profileImageButton}
              onClick={triggerFileInput}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Camera size={18} />
            </button>
            
            <input 
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              accept="image/*"
            />
          </div>
          
          <h2 style={styles.profileHeaderName}>{userData?.username || 'User'}</h2>
          <div style={styles.profileHeaderEmail}>
            <Mail size={16} />
            {userData?.email || 'email@example.com'}
          </div>
        </div>
        
        <div style={{
          ...styles.card,
          boxShadow: 'none',
          marginBottom: theme.spacing.lg,
          background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
        }}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <User size={20} style={{ color: theme.colors.primary }} />
              Account Information
            </h3>
          </div>
          
          <div style={styles.profileInfoGrid}>
            <div style={styles.profileInfoCard}>
              <div style={styles.profileInfoLabel}>Username</div>
              <div style={styles.profileInfoValue}>{userData?.username || 'User'}</div>
            </div>
            
            <div style={styles.profileInfoCard}>
              <div style={styles.profileInfoLabel}>Email</div>
              <div style={styles.profileInfoValue}>{userData?.email || 'email@example.com'}</div>
            </div>
            
            <div style={styles.profileInfoCard}>
              <div style={styles.profileInfoLabel}>Status</div>
              <div style={styles.profileInfoValue}>
                <span style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: theme.colors.success,
                  animation: 'pulse 2s infinite',
                }}></span>
                {userData?.status || 'Active'}
              </div>
            </div>
            
            <div style={styles.profileInfoCard}>
              <div style={styles.profileInfoLabel}>Role</div>
              <div style={styles.profileInfoValue}>
                <Settings size={16} style={{ opacity: 0.7 }} />
                {userData?.role === 'ROLE_ADMIN' ? 'Administrator' : 'User'}
              </div>
            </div>
          </div>
        </div>
        
        {activeCamp && (
          <div style={{
            ...styles.card,
            boxShadow: 'none',
            marginBottom: theme.spacing.lg,
            background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
          }}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <Calendar size={20} style={{ color: theme.colors.primary }} />
                Current Camp
              </h3>
            </div>
            
            <div style={{
              padding: '25px',
              background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
              borderRadius: theme.borderRadius.large,
              border: `1px solid ${theme.colors.divider}`,
            }}>
              <h3 style={{
                fontSize: '22px',
                fontWeight: 700,
                margin: '0 0 8px 0',
                color: theme.colors.textPrimary,
              }}>{activeCamp.title}</h3>
              
              <div style={{
                fontSize: '15px',
                color: theme.colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 0 15px 0',
              }}>
                <Calendar size={16} style={{ opacity: 0.7 }} />
                <span>
                  {new Date(activeCamp.startDate).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })} - 
                  {new Date(activeCamp.endDate).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              </div>
              
              {activeCamp.description && (
                <p style={{
                  color: theme.colors.textSecondary,
                  marginBottom: '20px',
                  lineHeight: 1.6,
                  padding: '15px',
                  background: darkMode ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.01)',
                  borderRadius: theme.borderRadius.medium,
                  border: `1px solid ${theme.colors.divider}`,
                }}>{activeCamp.description}</p>
              )}
              
              {activeCamp.currentPhase && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '20px',
                  padding: '12px 16px',
                  background: darkMode ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.01)',
                  borderRadius: theme.borderRadius.medium,
                  border: `1px solid ${theme.colors.divider}`,
                }}>
                  <span style={{
                    color: theme.colors.textTertiary,
                    fontSize: '14px'
                  }}>Current Phase:</span>
                  <span style={{
                    fontWeight: '600',
                    color: theme.colors.primary,
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: theme.colors.primary,
                      boxShadow: '0 0 0 2px rgba(255, 59, 48, 0.3)',
                      animation: 'pulse 2s infinite',
                    }}></div>
                    {activeCamp.currentPhase.name || (
                      activeCamp.currentPhase.phaseType === 'PRE_CAMP' ? 'Pre-Camp' :
                      activeCamp.currentPhase.phaseType === 'MAIN_CAMP' ? 'Main Camp' :
                      activeCamp.currentPhase.phaseType === 'POST_CAMP' ? 'Post-Camp' : 
                      'Active Phase'
                    )}
                  </span>
                </div>
              )}
              
              <button 
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  background: theme.gradients.subtle,
                  color: theme.colors.primary,
                  border: `1px solid ${darkMode ? 'rgba(255, 59, 48, 0.3)' : 'rgba(255, 59, 48, 0.2)'}`,
                  boxShadow: '0 4px 10px rgba(255, 59, 48, 0.1)',
                }}
                onClick={() => {
                  if (activeCamp.currentPhase?.phaseType === 'PRE_CAMP') {
                    changeView('pre-camp');
                  } else if (activeCamp.currentPhase?.phaseType === 'POST_CAMP') {
                    changeView('post-camp');
                  } else {
                    changeView('calendar');
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 15px rgba(255, 59, 48, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(255, 59, 48, 0.1)';
                }}
              >
                <Calendar size={18} /> 
                {activeCamp.currentPhase?.phaseType === 'PRE_CAMP' 
                  ? 'View Pre-Camp' 
                  : activeCamp.currentPhase?.phaseType === 'POST_CAMP'
                    ? 'View Post-Camp'
                    : 'View Schedule'}
              </button>
            </div>
          </div>
        )}
        
        <div style={styles.profileActions}>
          <button 
            style={{
              ...styles.button,
              ...styles.primaryButton,
            }}
            onClick={() => changeView('edit')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = theme.shadows.buttonHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = theme.shadows.button;
            }}
          >
            <Edit3 size={18} /> Edit Profile
          </button>
          
          <button 
            style={{
              ...styles.button,
              ...styles.dangerButton,
            }}
            onClick={() => setShowDeleteConfirm(true)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 6px 15px rgba(255, 69, 58, 0.15)';
              e.currentTarget.style.background = 'rgba(255, 59, 48, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.background = 'rgba(255, 59, 48, 0.1)';
            }}
          >
            <Trash2 size={18} /> Delete Account
          </button>
        </div>
      </div>
    </div>
  );

  // Edit profile view
  const renderEditProfileView = () => (
    <div style={styles.dashboardContainer}>
      <div style={{
        ...styles.card,
        padding: windowWidth < 576 ? '20px' : '30px',
      }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: 800,
          marginBottom: '30px',
          color: theme.colors.primary,
          background: theme.gradients.primary,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>Edit Profile</h2>
        
        <form onSubmit={handleSaveProfile}>
          <div style={{
            ...styles.card,
            boxShadow: 'none',
            marginBottom: theme.spacing.lg,
            background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
          }}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <User size={20} style={{ color: theme.colors.primary }} />
                Personal Information
              </h3>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                <User size={16} />
                Username
              </label>
              <input
                type="text"
                style={{
                  ...styles.formInput,
                  ...(inputFocus === 'edit-username' && styles.formInputFocused)
                }}
                placeholder="Your username"
                value={editData.username}
                onChange={(e) => setEditData({...editData, username: e.target.value})}
                onFocus={() => setInputFocus('edit-username')}
                onBlur={() => setInputFocus(null)}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                <Mail size={16} />
                Email Address
              </label>
              <input
                type="email"
                style={{
                  ...styles.formInput,
                  ...(inputFocus === 'edit-email' && styles.formInputFocused)
                }}
                placeholder="your@email.com"
                value={editData.email}
                onChange={(e) => setEditData({...editData, email: e.target.value})}
                onFocus={() => setInputFocus('edit-email')}
                onBlur={() => setInputFocus(null)}
                required
              />
            </div>
          </div>
          
          <div style={{
            ...styles.card,
            boxShadow: 'none',
            marginBottom: theme.spacing.lg,
            background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '20px',
            }}>
              <h3 style={styles.cardTitle}>
                <Lock size={20} style={{ color: theme.colors.primary }} />
                Change Password
              </h3>
              <span style={{
                fontSize: '14px',
                color: theme.colors.textTertiary,
                fontStyle: 'italic',
              }}>
                (optional)
              </span>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                <Key size={16} />
                Current Password
              </label>
              <div style={styles.passwordInput}>
                <input
                  type={showPassword ? "text" : "password"}
                  style={{
                    ...styles.formInput,
                    ...(inputFocus === 'edit-current-password' && styles.formInputFocused)
                  }}
                  placeholder="Your current password"
                  value={editData.oldPassword}
                  onChange={(e) => setEditData({...editData, oldPassword: e.target.value})}
                  onFocus={() => setInputFocus('edit-current-password')}
                  onBlur={() => setInputFocus(null)}
                />
                <button 
                  type="button" 
                  style={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                <Key size={16} />
                New Password
              </label>
              <div style={styles.passwordInput}>
                <input
                  type={showPassword ? "text" : "password"}
                  style={{
                    ...styles.formInput,
                    ...(inputFocus === 'edit-new-password' && styles.formInputFocused)
                  }}
                  placeholder="New password"
                  value={editData.newPassword}
                  onChange={(e) => setEditData({...editData, newPassword: e.target.value})}
                  onFocus={() => setInputFocus('edit-new-password')}
                  onBlur={() => setInputFocus(null)}
                />
                <button 
                  type="button" 
                  style={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p style={styles.formHint}>Must be at least 6 characters</p>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                <Key size={16} />
                Confirm New Password
              </label>
              <div style={styles.passwordInput}>
                <input
                  type={showPassword ? "text" : "password"}
                  style={{
                    ...styles.formInput,
                    ...(inputFocus === 'edit-confirm-password' && styles.formInputFocused)
                  }}
                  placeholder="Confirm new password"
                  value={editData.confirmPassword}
                  onChange={(e) => setEditData({...editData, confirmPassword: e.target.value})}
                  onFocus={() => setInputFocus('edit-confirm-password')}
                  onBlur={() => setInputFocus(null)}
                />
              </div>
            </div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: theme.spacing.md,
          }}>
            <button 
              type="button" 
              style={{
                ...styles.button,
                ...styles.secondaryButton,
              }}
              onClick={() => changeView('profile')}
            >
              <X size={18} /> Cancel
            </button>
            
            <button 
              type="submit" 
              style={{
                ...styles.button,
                ...styles.primaryButton,
                ...(loading && styles.buttonDisabled)
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

   // Notifications view
   const renderNotificationsView = () => (
    <div style={styles.dashboardContainer}>
      <div style={{
        ...styles.card,
        padding: windowWidth < 576 ? '20px' : '30px',
      }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: 800,
          marginBottom: '30px',
          color: theme.colors.primary,
          background: theme.gradients.primary,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>Notifications</h2>
        
        {notifications.length > 0 ? (
          <>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              flexDirection: windowWidth < 576 ? 'column' : 'row',
              gap: windowWidth < 576 ? '15px' : '0'
            }}>
              <div style={{
                fontSize: '15px',
                color: theme.colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <Bell size={18} style={{ opacity: 0.8 }} />
                {notifications.length} Notification{notifications.length !== 1 ? 's' : ''}
              </div>
              
              <button 
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  padding: '10px 16px',
                  width: 'auto',
                }}
                onClick={async () => {
                  try {
                    await api('/api/auth/notifications/mark-all-read', 'POST');
                    
                    // Refresh notifications
                    fetchNotifications();
                    
                    showSuccess('All notifications marked as read');
                  } catch (err) {
                    console.error('Error marking all as read:', err);
                    showError('Error marking notifications as read');
                  }
                }}
              >
                <Check size={16} /> Mark All as Read
              </button>
            </div>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              maxHeight: windowWidth < 768 ? '500px' : '600px',
              overflowY: 'auto',
              padding: '5px',
            }}>
              {notifications.map((notification, index) => (
                <div 
                  key={notification.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '15px',
                    padding: '20px',
                    background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
                    borderRadius: theme.borderRadius.medium,
                    border: `1px solid ${theme.colors.divider}`,
                    cursor: 'pointer',
                    transition: theme.transitions.medium,
                    position: 'relative',
                    opacity: notification.isRead ? 0.7 : 1,
                    animation: `fadeIn 0.5s ease ${index * 0.05}s both`,
                  }}
                  onClick={() => markNotificationAsRead(notification.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = theme.shadows.medium;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: theme.borderRadius.medium,
                    background: `linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.secondary}20)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.colors.primary,
                    flexShrink: 0,
                  }}>
                    {notification.type === 'system' ? <Info size={24} /> : <Bell size={24} />}
                  </div>
                  
                  <div style={{
                    flex: 1,
                    minWidth: 0,
                  }}>
                    <p style={{
                      margin: '0 0 8px 0',
                      lineHeight: 1.5,
                      color: theme.colors.textPrimary,
                      fontWeight: notification.isRead ? 400 : 500,
                    }}>{notification.message}</p>
                    
                    <div style={{
                      fontSize: '13px',
                      color: theme.colors.textTertiary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                      <Activity size={14} style={{ opacity: 0.7 }} />
                      {formatDate(notification.timestamp)}
                    </div>
                  </div>
                  
                  {!notification.isRead && (
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: theme.colors.primary,
                      position: 'absolute',
                      top: '20px',
                      right: '20px',
                      boxShadow: '0 0 0 3px rgba(255, 59, 48, 0.2)',
                      animation: 'pulse 2s infinite',
                    }}></div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{
            background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
            borderRadius: theme.borderRadius.large,
            padding: '50px 20px',
            textAlign: 'center',
            border: `1px solid ${theme.colors.divider}`,
            animation: 'fadeIn 0.5s ease-in-out',
          }}>
            <div style={{
              marginBottom: '20px',
              color: theme.colors.textTertiary,
              animation: 'pulse 2s infinite ease-in-out',
              display: 'flex',
              justifyContent: 'center',
            }}>
              <Bell size={64} />
            </div>
            <h3 style={{
              fontSize: '22px',
              fontWeight: 700,
              marginBottom: '12px',
              color: theme.colors.textPrimary,
            }}>No Notifications</h3>
            <p style={{
              color: theme.colors.textSecondary,
              maxWidth: '400px',
              margin: '0 auto',
            }}>
              You currently have no notifications.
            </p>
          </div>
        )}
        
        <button 
          style={{
            ...styles.button,
            ...styles.secondaryButton,
            marginTop: '25px',
          }}
          onClick={() => changeView('dashboard')}
        >
          <ArrowLeft size={18} /> Back to Dashboard
        </button>
      </div>
    </div>
  );

  // Post-Camp view
  const renderPostCampView = () => (
    <div style={{
      animation: 'fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
      maxWidth: windowWidth < 992 ? '100%' : '900px',
      margin: '0 auto',
      width: '100%',
      padding: windowWidth < 576 ? '0 10px' : '0 20px',
    }}>
      <div style={{
        ...styles.card,
        background: darkMode 
          ? 'linear-gradient(to bottom right, rgba(31, 41, 55, 0.95), rgba(31, 41, 55, 0.85))'
          : 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = darkMode 
          ? '0 15px 35px rgba(0, 0, 0, 0.4)'
          : '0 15px 35px rgba(0, 0, 0, 0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = darkMode 
          ? '0 8px 25px rgba(0, 0, 0, 0.3)'
          : '0 8px 25px rgba(0, 0, 0, 0.08)';
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.5,
          background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.08), rgba(255, 59, 48, 0.03))',
          zIndex: 0,
        }}></div>
        
        <div style={{position: 'relative', zIndex: 1}}>
          {handlePostCampView()}

          <button 
            style={{
              ...styles.primaryButton,
              marginTop: '25px',
              background: 'linear-gradient(145deg, #FF3B30, #FF2D55)',
            }}
            onClick={() => changeView('dashboard')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 59, 48, 0.4), 0 2px 5px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 59, 48, 0.3), 0 1px 2px rgba(0, 0, 0, 0.1)';
            }}
          >
            <ArrowLeft size={18} /> Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      {/* Background graphics */}
      <div style={styles.backgroundGradient} ref={backgroundRef}></div>
      <div style={styles.backgroundPattern}></div>

      {/* Mobile header */}
      <div style={styles.mobileHeader}>
        <button 
          style={{
            ...styles.mobileMenuButton,
            background: darkMode ? 'rgba(255, 59, 48, 0.15)' : 'rgba(255, 59, 48, 0.1)', 
            border: '1px solid rgba(255, 59, 48, 0.2)',
          }}
          onClick={() => setShowSidebar(!showSidebar)}        >
          <Menu size={24} color="#FF3B30" />
        </button>
        
        <h1 style={styles.mobileHeaderTitle}>
          {isLoggedIn ? 
            `${userData?.username || 'Admin'} Dashboard` : 
            'Camp Admin Dashboard'}
          {isLoggedIn && (
            <span style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: theme.colors.success,
              marginLeft: '6px',
              animation: 'pulse 2s infinite',
            }}></span>
          )}
        </h1>
        
        <div style={{
          display: 'flex',
          gap: '10px',
        }}>
          <button
            style={styles.mobileMenuButton}
            onClick={toggleDarkMode}
          >
            {darkMode ? <Sun size={22} /> : <Moon size={22} />}
          </button>
          
          <button 
            style={{
              ...styles.mobileMenuButton,
              background: darkMode ? 'rgba(255, 59, 48, 0.15)' : 'rgba(255, 59, 48, 0.1)',
              color: '#FF3B30',
            }}
            onClick={handleClose}
          >
            <ArrowLeft size={22} />
          </button>
        </div>
      </div>
      
      
      <div style={styles.sidebar} ref={sidebarRef}>
        <div style={styles.sidebarHeader}>
          <div style={styles.logoText}>cAmPP</div>
          
          {isLoggedIn && userData && (
            <div style={styles.sidebarProfile}>
              <div style={styles.profileAvatar}>
                {imageLoading ? (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                    borderRadius: '50%',
                    animation: 'pulse 1.5s infinite ease-in-out',
                  }}>
                    <Loader size={20} color={theme.colors.textTertiary} />
                  </div>
                ) : profileImage && !profileImageError ? (
                  <img
                    src={profileImage}
                    alt={userData.username || 'User'}
                    style={styles.profileImage}
                    onError={() => setProfileImageError(true)}
                  />
                ) : (
                  <div 
                    style={{
                      ...styles.profileInitials,
                      backgroundColor: generateProfileColor(userData.username),
                      color: useLightText(generateProfileColor(userData.username)) ? '#FFFFFF' : '#000000'
                    }}
                  >
                    {getInitials(userData.username)}
                  </div>
                )}
                <div style={styles.profileStatus} />
              </div>
              
              <div style={styles.profileInfo}>
                <p style={styles.profileName}>{userData.username}</p>
                <p style={styles.profileEmail}>{userData.email}</p>
              </div>
            </div>
          )}
        </div>
        
        <div style={styles.menuSection}>
          {isLoggedIn ? (
            <>
              <div style={styles.menuCategory}>MAIN MENU</div>
              
              <button 
                style={{
                  ...styles.menuItem,
                  ...(activeView === 'dashboard' && styles.menuItemActive)
                }}
                onClick={() => changeView('dashboard')}
                onMouseEnter={(e) => {
                  if (activeView !== 'dashboard') {
                    e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeView !== 'dashboard') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }
                }}
              >
                <div style={styles.menuIcon}>
                  <Home size={20} />
                </div>
                <span>Dashboard</span>
                {activeView === 'dashboard' && (
                  <div style={styles.menuIndicator}></div>
                )}
              </button>
              
              <div style={styles.menuCategory}>ACCOUNT</div>
              
              <button 
                style={{
                  ...styles.menuItem,
                  ...(activeView === 'profile' && styles.menuItemActive)
                }}
                onClick={() => changeView('profile')}
                onMouseEnter={(e) => {
                  if (activeView !== 'profile') {
                    e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeView !== 'profile') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }
                }}
              >
                <div style={styles.menuIcon}>
                  <User size={20} />
                </div>
                <span>My Profile</span>
                {activeView === 'profile' && (
                  <div style={styles.menuIndicator}></div>
                )}
              </button>
              
              <button 
                style={{
                  ...styles.menuItem,
                  ...(activeView === 'edit' && styles.menuItemActive)
                }}
                onClick={() => changeView('edit')}
                onMouseEnter={(e) => {
                  if (activeView !== 'edit') {
                    e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeView !== 'edit') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }
                }}
              >
                <div style={styles.menuIcon}>
                  <Edit3 size={20} />
                </div>
                <span>Edit Profile</span>
                {activeView === 'edit' && (
                  <div style={styles.menuIndicator}></div>
                )}
              </button>
              {isLoggedIn && userData && userData.role === 'ROLE_ADMIN' && (
  <>
    <div style={styles.menuCategory}>FEEDBACK</div>
    
    <button 
      style={{
        ...styles.menuItem,
        ...(activeView === 'feedback' && styles.menuItemActive)
      }}
      onClick={() => changeView('feedback')}
      onMouseEnter={(e) => {
        if (activeView !== 'feedback') {
          e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
          e.currentTarget.style.transform = 'translateX(5px)';
        }
      }}
      onMouseLeave={(e) => {
        if (activeView !== 'feedback') {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.transform = 'translateX(0)';
        }
      }}
    >
      <div style={styles.menuIcon}>
        <MessageCircle size={20} />
      </div>
      <span>Feedback Management</span>
      {activeView === 'feedback' && (
        <div style={styles.menuIndicator}></div>
      )}
    </button>
  </>
)}

              <button 
                style={{
                  ...styles.menuItem,
                  ...(activeView === 'notifications' && styles.menuItemActive)
                }}
                onClick={() => changeView('notifications')}
                onMouseEnter={(e) => {
                  if (activeView !== 'notifications') {
                    e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeView !== 'notifications') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }
                }}
              >
                <div style={styles.menuIcon}>
                  <Bell size={20} />
                </div>
                <span>Notifications</span>
                {unreadNotificationCount > 0 && (
                  <div style={{
                    background: theme.colors.primary,
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: '600',
                    minWidth: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 'auto',
                    padding: '0 6px',
                  }}>{unreadNotificationCount}</div>
                )}
                {activeView === 'notifications' && (
                  <div style={styles.menuIndicator}></div>
                )}
              </button>
              
              <div style={styles.menuCategory}>CAMP</div>
              
              {activeCamp && (
                <button 
                  style={{
                    ...styles.menuItem,
                    ...((activeView === 'calendar' || activeView === 'pre-camp' || activeView === 'post-camp') && styles.menuItemActive)
                  }}
                  onClick={() => {
                    if (activeCamp.currentPhase?.phaseType === 'PRE_CAMP') {
                      changeView('pre-camp');
                    } else if (activeCamp.currentPhase?.phaseType === 'POST_CAMP') {
                      changeView('post-camp');
                    } else {
                      changeView('calendar');
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (activeView !== 'calendar' && activeView !== 'pre-camp' && activeView !== 'post-camp') {
                      e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                      e.currentTarget.style.transform = 'translateX(5px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeView !== 'calendar' && activeView !== 'pre-camp' && activeView !== 'post-camp') {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }
                  }}
                >
                  <div style={styles.menuIcon}>
                    <Calendar size={20} />
                  </div>
                  <span>
                    {activeCamp.currentPhase?.phaseType === 'PRE_CAMP' 
                      ? 'Pre-Camp' 
                      : activeCamp.currentPhase?.phaseType === 'POST_CAMP'
                        ? 'Post-Camp'
                        : 'Schedule'}
                  </span>
                  {(activeView === 'calendar' || activeView === 'pre-camp' || activeView === 'post-camp') && (
                    <div style={styles.menuIndicator}></div>
                  )}
                </button>
              )}
              
              <div style={styles.menuCategory}>SETTINGS</div>
              
              <button 
                style={styles.menuItem}
                onClick={() => setShowTerms(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div style={styles.menuIcon}>
                  <FileText size={20} />
                </div>
                <span>Terms of Service</span>
              </button>
              
              <button 
                style={styles.menuItem}
                onClick={() => setShowPrivacyPolicy(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div style={styles.menuIcon}>
                  <Shield size={20} />
                </div>
                <span>Privacy Policy</span>
              </button>
            </>
          ) : (
            <>
              <div style={styles.menuCategory}>ACCESS</div>
              
              <button 
                style={{
                  ...styles.menuItem,
                  ...(activeView === 'login' && styles.menuItemActive)
                }}
                onClick={() => changeView('login')}
                onMouseEnter={(e) => {
                  if (activeView !== 'login') {
                    e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeView !== 'login') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }
                }}
              >
                <div style={styles.menuIcon}>
                  <User size={20} />
                </div>
                <span>Sign In</span>
                {activeView === 'login' && (
                  <div style={styles.menuIndicator}></div>
                )}
              </button>
              
              <button 
                style={{
                  ...styles.menuItem,
                  ...(activeView === 'register' && styles.menuItemActive)
                }}
                onClick={() => changeView('register')}
                onMouseEnter={(e) => {
                  if (activeView !== 'register') {
                    e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeView !== 'register') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }
                }}
              >
                <div style={styles.menuIcon}>
                  <UserCheck size={20} />
                </div>
                <span>Create Account</span>
                {activeView === 'register' && (
                  <div style={styles.menuIndicator}></div>
                )}
              </button>
              
              <div style={styles.menuCategory}>INFO</div>
              
              <button 
                style={styles.menuItem}
                onClick={() => setShowTerms(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div style={styles.menuIcon}>
                  <FileText size={20} />
                </div>
                <span>Terms of Service</span>
              </button>
              
              <button 
                style={styles.menuItem}
                onClick={() => setShowPrivacyPolicy(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div style={styles.menuIcon}>
                  <Shield size={20} />
                </div>
                <span>Privacy Policy</span>
              </button>
            </>
          )}
        </div>
        
        <div style={styles.sidebarFooter}>
          <button 
            style={styles.darkModeToggle}
            onClick={toggleDarkMode}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
            }}
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          
          {isLoggedIn && (
            <button 
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                background: 'transparent',
                padding: '8px 12px',
                width: 'auto',
              }}
              onClick={handleLogout}
              title="Sign Out"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
                e.currentTarget.style.transform = 'translateX(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          )}
        </div>
      </div>
      {windowWidth > 768 && <BackButton onClick={handleClose} />}
      
      {/* Add floating close button for all screen sizes */}
      <FloatingCloseButton onClick={handleClose} />
      
      {/* Main content */}
      <main style={styles.mainContent}>
        {/* Render content based on active view */}
        {!isLoggedIn && activeView === 'login' && renderLoginView()}
        {!isLoggedIn && activeView === 'register' && renderRegisterView()}
        {isLoggedIn && activeView === 'dashboard' && renderDashboardView()}
        {isLoggedIn && activeView === 'profile' && renderProfileView()}
        {isLoggedIn && activeView === 'edit' && renderEditProfileView()}
        {isLoggedIn && activeView === 'notifications' && renderNotificationsView()}
        {isLoggedIn && activeView === 'calendar' && renderCalendarView()}
        {isLoggedIn && activeView === 'red-schedule' && renderRedSchedule()}

        {isLoggedIn && activeView === 'feedback' && renderFeedbackManagementView()}


      </main>
      
      {/* Notifications */}
      {error && (
        <div style={{
          ...styles.notification,
          ...styles.notificationError,
        }}>
          <div style={styles.notificationIcon}>
            <AlertTriangle size={24} color={theme.colors.error} />
          </div>
          <div style={styles.notificationContent}>
            <p style={styles.notificationMessage}>{error}</p>
          </div>
        </div>
      )}
      
      {successMessage && (
        <div style={{
          ...styles.notification,
          ...styles.notificationSuccess,
        }}>
          <div style={styles.notificationIcon}>
            <CheckCircle size={24} color={theme.colors.success} />
          </div>
          <div style={styles.notificationContent}>
            <p style={styles.notificationMessage}>{successMessage}</p>
          </div>
        </div>
      )}
      
      {/* Loading overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          animation: 'fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          <div style={{
            background: darkMode ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            borderRadius: theme.borderRadius.large,
            padding: '30px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '15px',
            boxShadow: theme.shadows.large,
            animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.colors.divider}`,
          }}>
            <div style={{
              animation: 'spin 1s linear infinite',
            }}>
              <Loader size={40} color={theme.colors.primary} />
            </div>
            <p style={{
              fontSize: '16px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              margin: 0,
            }}>
              Loading...
            </p>
          </div>
        </div>
      )}
      
      {/* Modals */}
      {showDeleteConfirm && <DeleteConfirmationModal />}
      {showTerms && <TermsOfService showTerms={showTerms} setShowTerms={setShowTerms} />}
      {showPrivacyPolicy && <PrivacyPolicy showPrivacyPolicy={showPrivacyPolicy} setShowPrivacyPolicy={setShowPrivacyPolicy} />}
      </div>
  );
};

export default UserManagementDashboard;